import { useState, useEffect } from "react";
import { GoogleOAuthProvider, useGoogleLogin, googleLogout } from "@react-oauth/google";
import MEMCApp from "./MEMC_App";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Decodifica el JWT de Google sin librería externa
function decodeJwt(token) {
  try {
    const base64 = token.split(".")[1].replace(/-/g,"+").replace(/_/g,"/");
    return JSON.parse(atob(base64));
  } catch { return null; }
}

// Módulos habilitados por plan
const PLAN_MODULOS = {
  free: ["nuevo","historial","calculador","memc","config"],
  pro:  ["nuevo","historial","calculador","memc","config","coord_prot","calidad_pot","arranque","flujos","cortocircuito"],
};

// ── CONFIGURACIÓN ─────────────────────────────────────────────
const ENDPOINT = process.env.REACT_APP_ENDPOINT;

// ── PALETA ELECTROHUB ─────────────────────────────────────────
const C = {
  blue:    "#0A2463",
  electric:"#1E88E5",
  orange:  "#FF6B00",
  orangeL: "rgba(255,107,0,.1)",
  blueL:   "rgba(10,36,99,.07)",
};

// ── CONFIGURACIÓN POR DEFECTO ─────────────────────────────────
const CFG_DEFAULT = {
  empresa:       "ElectroHub Ingeniería S.A.S.",
  firmante:      "Ing. Juan D. Marín J.",
  titulo:        "Ingeniero Electricista · RETIE",
  email:         "proyectos@electrohub.app",
  web:           "www.electrohub.app",
  ciudad:        "Bogotá",
  colorPrimario: "#0A2463",
  colorAcento:   "#FF6B00",
};

// ── DATOS ─────────────────────────────────────────────────────
const TIPOS = [
  { id:"residencial",    icon:"🏠", label:"Residencial",       desc:"Aptos., casas, conjuntos" },
  { id:"comercial",      icon:"🏢", label:"Comercial",          desc:"Oficinas, locales, centros" },
  { id:"industrial",     icon:"🏭", label:"Industrial",         desc:"Bodegas, plantas, procesos" },
  { id:"infraestructura",icon:"🛣️", label:"Infraestructura",    desc:"Vías, redes, alumbrado" },
];

const DISC_ACTIVAS = {
  E: {
    label: "Eléctrica — Core ElectroHub",
    sub:   "Fuerza, iluminación, MT, IoT",
    color: C.electric,
    items: [
      "Selección de conductores","Iluminación + DALI","Media Tensión / Subestación",
"Planta de Emergencia","Cableado Estructurado","Coordinación de protecciones",
"Solar FV","Automatización / IoT","RETIE / RETILAP",
      "Pararrayos / Apantallamiento","Red Tierra / Equipotencial",
    ],
  },
  G: {
    label: "Gestión ElectroHub",
    sub:   "Presupuesto, BIM, interventoría",
    color: C.orange,
    items: [
      "Presupuesto Eléctrico","Coordinación BIM","Especificaciones Técnicas",
      "Interventoría Eléctrica","Actas ElectroHub",
      "Estudio de Cargabilidad","Calidad de Energía",
    ],
  },
};

const DISC_DESHABILITADAS = [
  { label:"Mecánica / HVAC",                items:["HVAC / Climatización","Red Contra Incendio","Cuarto de Máquinas","Gas Natural / GLP","Ventilación Industrial"] },
  { label:"Plomería / Instalaciones Sanitarias", items:["Red Hidrosanitaria","Aguas Residuales","Aguas Lluvias","Planta de Tratamiento","Riego y Paisajismo"] },
];

const OPERADORES_MAP = {
  // Cundinamarca / Bogotá
  bogotá:"ENEL/CODENSA", bogota:"ENEL/CODENSA", soacha:"ENEL/CODENSA", zipaquirá:"ENEL/CODENSA", zipaquira:"ENEL/CODENSA", facatativá:"ENEL/CODENSA", facatativa:"ENEL/CODENSA", chía:"ENEL/CODENSA", chia:"ENEL/CODENSA", mosquera:"ENEL/CODENSA", madrid:"ENEL/CODENSA", funza:"ENEL/CODENSA",
  // Antioquia
  medellín:"EPM", medellin:"EPM", bello:"EPM", itagüí:"EPM", itagui:"EPM", envigado:"EPM", sabaneta:"EPM", rionegro:"EPM", caldas:"EPM",
  // Eje Cafetero
  manizales:"CHEC EPM", villamaría:"CHEC EPM", villamaria:"CHEC EPM", chinchiná:"CHEC EPM", chinchina:"CHEC EPM",
  pereira:"EEP / CHEC EPM", dosquebradas:"EEP / CHEC EPM", "santa rosa de cabal":"EEP / CHEC EPM",
  armenia:"EEQ EPM", calarcá:"EEQ EPM", calarca:"EEQ EPM",
  // Valle del Cauca
  cali:"EMCALI / EPSA (Celsia)", palmira:"EPSA (Celsia)", buenaventura:"EPSA (Celsia)", tuluá:"EPSA (Celsia)", tulua:"EPSA (Celsia)", buga:"EPSA (Celsia)", cartago:"EPSA (Celsia)",
  // Santander
  bucaramanga:"ESSA", floridablanca:"ESSA", girón:"ESSA", giron:"ESSA", piedecuesta:"ESSA",
  // Norte de Santander
  cúcuta:"CENS", cucuta:"CENS", pamplona:"CENS", ocaña:"CENS", ocana:"CENS",
  // Atlántico / Costa Caribe
  barranquilla:"Air-e", soledad:"Air-e", malambo:"Air-e",
  cartagena:"Air-e", magangué:"Air-e", magangue:"Air-e",
  "santa marta":"Air-e", riohacha:"Air-e", valledupar:"Air-e", montería:"AFINIA", monteria:"AFINIA", sincelejo:"AFINIA",
  // Tolima
  ibagué:"ENERTOLIMA", ibague:"ENERTOLIMA", espinal:"ENERTOLIMA",
  // Huila
  neiva:"ELECTROHUILA",
  // Cauca / Nariño
  popayán:"CEDELCA / EPSA", popayan:"CEDELCA / EPSA",
  pasto:"CEDENAR", túquerres:"CEDENAR", tuquerres:"CEDENAR",
  // Boyacá
  tunja:"EBSA", duitama:"EBSA", sogamoso:"EBSA",
  // Llanos / Orinoquia
  villavicencio:"EMSA", acacías:"EMSA", acacias:"EMSA",
  yopal:"ENERCA", arauca:"ENELAR",
  // Amazonia
  florencia:"ELECTROCAQUETÁ", leticia:"EDAM",
  // Pacífico
  quibdó:"DISPAC", quibdo:"DISPAC",
};

function getOperador(ciudad) {
  if (!ciudad) return null;
  const key = ciudad.toLowerCase().trim();
  return OPERADORES_MAP[key] || null;
}

const MODULOS = [
  { id:"memorias",        icon:"📐", label:"Memorias de Cálculo",    desc:"RETIE 2024 · cuadros de carga · criterios de diseño", color:C.electric },
  { id:"planos_uso_final",icon:"📋", label:"Planos de Uso Final",     desc:"Formatos de Plantillas ACAD, REVIT (convenciones, familias)", color:C.electric },
  { id:"planos_or",       icon:"🏛️", label:"Planos aprobación OR",    desc:"Operador de Red · CODENSA · EPM · EMCALI",            color:C.orange   },
  { id:"solar_fv",        icon:"☀️", label:"Solar FV",                desc:"Fotovoltaico · UPME · estudio de sombras",            color:"#F59E0B"  },
];

// ── LOGO SVG ──────────────────────────────────────────────────
function BoltLogo({ size = 38, white = false }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"
        fill={white ? "rgba(255,107,0,.9)" : C.orange}
        stroke={white ? "#fff" : C.blue}
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── CAMPO DE TEXTO ─────────────────────────────────────────────
function Field({ label, value, onChange, placeholder, multiline, half }) {
  const [focus, setFocus] = useState(false);
  const base = {
    width: "100%", padding: "9px 12px",
    border: `0.5px solid ${focus ? C.electric : "#E0E0E0"}`,
    borderRadius: 7, fontSize: 13, fontFamily: "inherit",
    outline: "none", background: "#fff", color: "#111",
    boxSizing: "border-box", lineHeight: 1.5,
    boxShadow: focus ? `0 0 0 3px rgba(30,136,229,.07)` : "none",
    transition: "border .15s, box-shadow .15s",
  };
  return (
    <div style={{ marginBottom: 14, flex: half ? "1" : undefined }}>
      <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#777", marginBottom:5 }}>
        {label}
      </label>
      {multiline
        ? <textarea value={value} onChange={e => onChange(e.target.value)}
            placeholder={placeholder} rows={3}
            style={{ ...base, resize:"none" }}
            onFocus={() => setFocus(true)} onBlur={() => setFocus(false)} />
        : <input value={value} onChange={e => onChange(e.target.value)}
            placeholder={placeholder} style={base}
            onFocus={() => setFocus(true)} onBlur={() => setFocus(false)} />
      }
    </div>
  );
}

// ── UPLOAD DE LOGO ────────────────────────────────────────────
function LogoUpload({ label, sub, icon, logo, onLogo, onRemove }) {
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    onLogo({ url, name: file.name });
  };

  return (
    <div
      onClick={() => !logo && document.getElementById(`lu-${label}`).click()}
      style={{
        border: `1.5px dashed ${logo ? C.electric : "#DDD"}`,
        borderRadius: 10, padding: "18px 14px", textAlign:"center",
        cursor: logo ? "default" : "pointer", background: logo ? "rgba(30,136,229,.03)" : "#fff",
        transition: "all .16s", position:"relative",
      }}
    >
      <input type="file" id={`lu-${label}`} accept="image/*"
        style={{ position:"absolute", inset:0, opacity:0, cursor:"pointer", display: logo ? "none" : "block" }}
        onChange={handleFile} />
      {logo ? (
        <>
          <img src={logo.url} alt="logo" style={{ maxHeight:44, maxWidth:"100%", objectFit:"contain", marginBottom:6 }} />
          <div style={{ fontSize:11, fontWeight:600, color:C.electric, marginBottom:4, wordBreak:"break-all" }}>{logo.name}</div>
          <button onClick={e => { e.stopPropagation(); onRemove(); }}
            style={{ fontSize:10, color:"#CC2222", background:"none", border:"none", cursor:"pointer", fontFamily:"inherit" }}>
            ✕ Quitar
          </button>
        </>
      ) : (
        <>
          <div style={{ fontSize:24, marginBottom:8 }}>{icon}</div>
          <div style={{ fontSize:12, fontWeight:600, marginBottom:3 }}>{label}</div>
          <div style={{ fontSize:10, color:"#999", marginBottom:4 }}>{sub}</div>
          <div style={{ fontSize:10, color:C.electric }}>PNG · SVG · fondo transparente</div>
        </>
      )}
    </div>
  );
}

// ── STEPPER ───────────────────────────────────────────────────
function Stepper({ step }) {
  const labels = ["Tipo","Datos y logos","Disciplinas","Resultado"];
  return (
    <div style={{ display:"flex", alignItems:"center", gap:4, marginBottom:24, flexWrap:"wrap" }}>
      {labels.map((l, i) => (
        <div key={i} style={{ display:"flex", alignItems:"center", gap:4 }}>
          <div style={{
            width:22, height:22, borderRadius:"50%", display:"grid", placeItems:"center",
            fontSize:10, fontWeight:700, flexShrink:0, transition:"all .2s",
            background: i < step ? C.orange : i === step ? C.blue : "transparent",
            color: i <= step ? "#fff" : "#aaa",
            border: i > step ? "1.5px solid #ddd" : "none",
          }}>
            {i < step ? "✓" : i + 1}
          </div>
          <span style={{ fontSize:11, fontWeight: i===step ? 600 : 400, color: i===step ? "#111" : "#aaa" }}>{l}</span>
          {i < labels.length - 1 && <div style={{ width:16, height:1, background:"#E0E0E0" }}/>}
        </div>
      ))}
    </div>
  );
}

// ── AI HINT ───────────────────────────────────────────────────
function AIHint({ text }) {
  return (
    <div style={{
      display:"flex", gap:10, background:"rgba(30,136,229,.04)",
      border:`0.5px solid rgba(30,136,229,.25)`, borderRadius:10,
      padding:"11px 14px", marginBottom:18,
    }}>
      <div style={{ width:7, height:7, borderRadius:"50%", background:C.electric, flexShrink:0, marginTop:4 }}/>
      <div>
        <div style={{ fontSize:9, fontWeight:700, letterSpacing:.6, textTransform:"uppercase", color:C.electric, marginBottom:3 }}>
          ElectroHub · Asistente IA
        </div>
        <div style={{ fontSize:12, lineHeight:1.6 }}>{text}</div>
      </div>
    </div>
  );
}

// ── MEMBRETE PREVIEW ──────────────────────────────────────────
function MembretePreview({ logoD, logoC, cliente, cfg = CFG_DEFAULT }) {
  return (
    <div style={{ border:"0.5px solid #E0E0E0", borderRadius:10, overflow:"hidden", marginBottom:20 }}>
      <div style={{ background:cfg.colorPrimario, padding:"10px 16px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          {logoD
            ? <img src={logoD.url} alt="d" style={{ height:24, width:"auto", objectFit:"contain" }}/>
            : <div style={{ background:"rgba(255,255,255,.12)", borderRadius:5, padding:"3px 8px", fontSize:9, color:"rgba(255,255,255,.5)", fontWeight:700 }}>DISEÑADOR</div>
          }
        </div>
        <div style={{ textAlign:"right" }}>
          <div style={{ color:"#fff", fontWeight:700, fontSize:13 }}>{cfg.empresa}</div>
          <div style={{ color:"rgba(255,255,255,.4)", fontSize:9, letterSpacing:.5 }}>{cfg.titulo}</div>
        </div>
      </div>
      <div style={{ height:3, background:cfg.colorAcento }}/>
      <div style={{ padding:"12px 16px", display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, background:"#fff" }}>
        <div>
          <div style={{ fontSize:9, color:"#aaa", textTransform:"uppercase", letterSpacing:.3, marginBottom:2 }}>Proyecto</div>
          <div style={{ fontSize:12, fontWeight:600 }}>Edificio Av. Principal</div>
        </div>
        <div>
          <div style={{ fontSize:9, color:"#aaa", textTransform:"uppercase", letterSpacing:.3, marginBottom:2 }}>Cliente</div>
          <div style={{ fontSize:12, fontWeight:600 }}>{cliente || "Cliente S.A.S."}</div>
        </div>
        <div>
          <div style={{ fontSize:9, color:"#aaa", textTransform:"uppercase", letterSpacing:.3, marginBottom:2 }}>Firmante</div>
          <div style={{ fontSize:12, fontWeight:600 }}>{cfg.firmante}</div>
        </div>
        <div>
          <div style={{ fontSize:9, color:"#aaa", textTransform:"uppercase", letterSpacing:.3, marginBottom:2 }}>Logo cliente</div>
          {logoC
            ? <img src={logoC.url} alt="c" style={{ maxHeight:20, maxWidth:80, objectFit:"contain" }}/>
            : <div style={{ fontSize:10, color:"#bbb", fontStyle:"italic" }}>Paso 2 →</div>
          }
        </div>
      </div>
      <div style={{ background:"#F8F8F8", borderTop:"0.5px solid #EEE", padding:"6px 16px", display:"flex", justifyContent:"space-between", fontSize:10, color:"#aaa" }}>
        <span>{cfg.empresa} · {cfg.email}</span>
        <span style={{ color:C.electric, fontWeight:600 }}>Confidencial</span>
      </div>
    </div>
  );
}

// ── LOGIN BUTTON (hook must live inside GoogleOAuthProvider) ──
function LoginButton({ onSuccess, onError, disabled }) {
  const login = useGoogleLogin({
    flow: "implicit",
    onSuccess: async tokenResponse => {
      try {
        const res  = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });
        const info = await res.json();
        onSuccess(info, tokenResponse.access_token);
      } catch(e) { onError(e.message || "Error obteniendo info de Google."); }
    },
    onError: () => onError("Error al iniciar sesión con Google."),
  });
  return (
    <button
      onClick={() => login()}
      disabled={disabled}
      style={{
        width:"100%", padding:"11px 16px", borderRadius:8, border:"1.5px solid #DADCE0",
        background:"#fff", cursor:disabled?"not-allowed":"pointer", display:"flex",
        alignItems:"center", justifyContent:"center", gap:10, fontSize:14, fontWeight:500,
        color:"#3c4043", fontFamily:"inherit", boxShadow:"0 1px 3px rgba(0,0,0,.08)",
        transition:"box-shadow .15s",
      }}
    >
      <svg width="18" height="18" viewBox="0 0 48 48">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      </svg>
      Iniciar sesión con Google
    </button>
  );
}

// ── LOGIN SCREEN ─────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [err, setErr]       = useState("");
  const [loading, setLoading] = useState(false);

  async function handleGoogleSuccess(info, accessToken) {
    setErr(""); setLoading(true);
    try {
      if (!info?.email) throw new Error("No se pudo obtener el email de Google.");
      const res  = await fetch(`${process.env.REACT_APP_ENDPOINT}?accion=login&email=${encodeURIComponent(info.email)}&nombre=${encodeURIComponent(info.name||"")}`, { method:"GET" });
      const data = await res.json();
      if (data.limiteSuperado) throw new Error("__LIMITE__");
      if (!data.ok) throw new Error(data.error || "Error al verificar usuario.");
      const user = { email: info.email, nombre: info.name||info.email, picture: info.picture||"", plan: data.plan, accessToken: accessToken || "" };
      localStorage.setItem("eh_user", JSON.stringify(user));
      onLogin(user);
    } catch(e) { setErr(e.message); }
    finally { setLoading(false); }
  }

  return (
    <div style={{ minHeight:"100vh", background:"#0A2463", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'DM Sans',system-ui,sans-serif" }}>
      <div style={{ background:"#fff", borderRadius:16, padding:"48px 40px", width:360, textAlign:"center", boxShadow:"0 24px 64px rgba(0,0,0,.25)" }}>
        <div style={{ fontSize:44, marginBottom:12 }}>⚡</div>
        <div style={{ fontWeight:800, fontSize:22, color:"#0A2463", marginBottom:4 }}>
          Electro<span style={{ color:"#FF6B00" }}>Hub</span>
        </div>
        <div style={{ fontSize:11, color:"#999", letterSpacing:.8, textTransform:"uppercase", marginBottom:32 }}>ProjectFiles · Colombia</div>
        <div style={{ fontSize:13, color:"#555", marginBottom:24, lineHeight:1.6 }}>
          Inicia sesión con tu cuenta Google para acceder a la plataforma.
        </div>
        {loading
          ? <div style={{ fontSize:13, color:"#888", padding:"12px 0" }}>Verificando cuenta…</div>
          : <LoginButton onSuccess={handleGoogleSuccess} onError={setErr} disabled={loading} />
        }
        {err && (
          err === "__LIMITE__" ? (
            <div style={{ marginTop:16, borderRadius:10, border:"1.5px solid rgba(255,107,0,.3)", background:"rgba(255,107,0,.06)", padding:"16px 18px", textAlign:"left" }}>
              <div style={{ fontWeight:700, fontSize:13, color:"#CC5500", marginBottom:6 }}>Capacidad máxima alcanzada</div>
              <div style={{ fontSize:12, color:"#555", lineHeight:1.6, marginBottom:12 }}>
                ElectroHub está en versión MVP con cupo limitado. Escríbenos para unirte a la lista de espera:
              </div>
              <a href="mailto:soporte@electrohub.app?subject=Lista de espera ElectroHub"
                style={{ display:"inline-block", background:"#0A2463", color:"#fff", padding:"8px 16px", borderRadius:7, fontSize:12, fontWeight:600, textDecoration:"none" }}>
                ✉ soporte@electrohub.app
              </a>
            </div>
          ) : (
            <div style={{ marginTop:16, fontSize:12, color:"#b82020", background:"#FADBD8", borderRadius:6, padding:"8px 12px" }}>{err}</div>
          )
        )}
        <div style={{ marginTop:32, fontSize:10, color:"#bbb" }}>
          Plan Free incluye: Nuevo proyecto · Historial<br/>
          Plan Pro: + Conductor económico · MEMC
        </div>
      </div>
    </div>
  );
}

// ── MODAL UPGRADE PRO ─────────────────────────────────────────
function UpgradeModal({ onClose }) {
  const WA = "https://wa.me/573108640585?text=" + encodeURIComponent("Hola, quiero activar el plan Pro de ElectroHub 🔌");
  return (
    <div onClick={onClose} style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,.55)", zIndex:1000,
      display:"flex", alignItems:"center", justifyContent:"center", padding:16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:"#fff", borderRadius:16, width:"100%", maxWidth:420,
        boxShadow:"0 24px 64px rgba(0,0,0,.25)", overflow:"hidden",
      }}>
        {/* Header */}
        <div style={{ background:"#0A2463", padding:"28px 28px 20px", textAlign:"center" }}>
          <div style={{ fontSize:36, marginBottom:8 }}>⚡</div>
          <div style={{ color:"#fff", fontWeight:800, fontSize:20, marginBottom:4 }}>
            Electro<span style={{ color:"#FF6B00" }}>Hub</span> <span style={{ color:"#FF9A4D" }}>Pro</span>
          </div>
          <div style={{ color:"rgba(255,255,255,.55)", fontSize:12 }}>Desbloquea todo el potencial de la plataforma</div>
        </div>

        {/* Features */}
        <div style={{ padding:"20px 28px" }}>
          <div style={{ fontSize:11, fontWeight:700, color:"#999", letterSpacing:.8, textTransform:"uppercase", marginBottom:12 }}>
            Incluido en Pro
          </div>
          {[
            ["✅","Todo lo del plan Free"],
            ["🛡️","Coordinación de protecciones"],
            ["📈","Calidad de la Potencia"],
            ["⚙️","Arranque de motores"],
            ["🔀","Flujos de Carga"],
            ["⚡","Cortocircuito"],
            ["🚀","Acceso prioritario a nuevos módulos"],
          ].map(([icon, text]) => (
            <div key={text} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:9, fontSize:13 }}>
              <span style={{ fontSize:16 }}>{icon}</span>
              <span style={{ color:"#222" }}>{text}</span>
            </div>
          ))}

          <div style={{ background:"rgba(255,107,0,.07)", border:"1.5px solid rgba(255,107,0,.25)", borderRadius:10, padding:"12px 16px", margin:"16px 0", textAlign:"center" }}>
            <div style={{ fontSize:11, color:"#FF6B00", fontWeight:600, marginBottom:2 }}>PRECIO MVP</div>
            <div style={{ fontSize:22, fontWeight:800, color:"#0A2463" }}>Consultar</div>
            <div style={{ fontSize:11, color:"#999" }}>Planes mensuales y anuales disponibles</div>
          </div>

          <a href={WA} target="_blank" rel="noreferrer" style={{
            display:"block", width:"100%", padding:"13px 0", borderRadius:9,
            background:"#25D366", color:"#fff", fontWeight:700, fontSize:14,
            textAlign:"center", textDecoration:"none", marginBottom:10,
            boxShadow:"0 4px 12px rgba(37,211,102,.35)",
          }}>
            💬 Solicitar Pro por WhatsApp
          </a>
          <button onClick={onClose} style={{
            width:"100%", padding:"10px 0", borderRadius:9,
            background:"none", border:"1.5px solid #E0E0E0",
            color:"#888", fontSize:13, cursor:"pointer", fontFamily:"inherit",
          }}>
            Ahora no
          </button>
        </div>
      </div>
    </div>
  );
}

// ── PRÓXIMAMENTE (módulos PRO en desarrollo) ──────────────────
const PRO_INFO = {
  coord_prot:   { icon:"🛡️", label:"Coordinación de Protecciones",  desc:"Curvas TCC, selectividad, ajuste de relés — RETIE 2024." },
  calidad_pot:  { icon:"📈", label:"Calidad de la Potencia",         desc:"Armónicos, flicker, desbalance — norma IEC 61000." },
  arranque:     { icon:"⚙️", label:"Arranque de Motores",            desc:"Arranque directo, estrella-triángulo, variador de frecuencia." },
  flujos:       { icon:"🔀", label:"Flujos de Carga",                desc:"Método Newton-Raphson — análisis de redes eléctricas." },
  cortocircuito:{ icon:"⚡", label:"Cortocircuito",                  desc:"Cálculo Icc máximo/mínimo — selección de protecciones." },
};

function ProximamenteView({ vista }) {
  const info = PRO_INFO[vista] || {};
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:400, textAlign:"center" }}>
      <div style={{ fontSize:56, marginBottom:16 }}>{info.icon}</div>
      <h2 style={{ fontSize:22, fontWeight:700, color:"#0A2463", marginBottom:8 }}>{info.label}</h2>
      <p style={{ fontSize:14, color:"#666", maxWidth:360, lineHeight:1.7, marginBottom:28 }}>{info.desc}</p>
      <div style={{
        display:"inline-flex", alignItems:"center", gap:8,
        background:"rgba(255,107,0,.1)", border:"1.5px solid rgba(255,107,0,.3)",
        borderRadius:10, padding:"10px 20px",
      }}>
        <span style={{ fontSize:13, fontWeight:600, color:"#FF6B00" }}>🚧 Módulo en desarrollo — disponible próximamente</span>
      </div>
    </div>
  );
}

// ── APP ───────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("eh_user")); } catch { return null; }
  });

  function handleLogout() {
    googleLogout();
    localStorage.removeItem("eh_user");
    setUser(null);
  }

  const plan    = user?.plan || "free";
  const allowed = PLAN_MODULOS[plan] || PLAN_MODULOS.free;

  const [cfg, setCfg] = useState(() => {
    try { return { ...CFG_DEFAULT, ...JSON.parse(localStorage.getItem("eh_config") || "{}") }; }
    catch { return CFG_DEFAULT; }
  });
  useEffect(() => { localStorage.setItem("eh_config", JSON.stringify(cfg)); }, [cfg]);

  const [step, setStep]     = useState(0);
  const [tipo, setTipo]     = useState("");
  const [subTipo, setSubTipo] = useState("");
  const [form, setForm]     = useState({ nombre:"Proyecto de prueba", cliente:"Constructora XYZ", codigo:"", ciudad: cfg.ciudad, area:"", pisos:"", desc:"" });
  const [logoD, setLogoD]   = useState(null);
  const [logoC, setLogoC]   = useState(null);
  const [discs, setDiscs]   = useState([]);
  const [modulos, setModulos] = useState([]);
  const [cadTipos, setCadTipos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadMsg, setLoadMsg] = useState("");
  const [result, setResult]   = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [error, setError]     = useState("");
  const [historial, setHistorial] = useState([]);
  const [vista, setVista]   = useState("nuevo");

  const HINTS = {
    "":             "Selecciona el tipo de proyecto para comenzar.",
    residencial:    "Para residencial preparo memorias eléctricas RETIE, cuadros de carga y planos con doble logo.",
    comercial:      "Para comercial incluyo RETIE/RETILAP, media tensión, BIM y cronograma — doble logo en todos.",
    industrial:     "Para industrial: diagrama unifilar, áreas clasificadas NFPA 70 y plan de mantenimiento eléctrico.",
    infraestructura:"Para infraestructura: alumbrado vial, red eléctrica y especificaciones técnicas con doble logo.",
  };

  const toggleDisc = (item) =>
    setDiscs(prev => prev.includes(item) ? prev.filter(d => d !== item) : [...prev, item]);

  const toggleModulo = (id) => {
    setModulos(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);
    if (id === "planos_uso_final") setCadTipos([]);
  };
  const toggleCadTipo = (id) =>
    setCadTipos(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);

  const reset = () => {
    setStep(0); setTipo(""); setSubTipo(""); setDiscs([]); setModulos([]); setCadTipos([]); setResult(null); setError("");
    setForm({ nombre:"Proyecto de prueba", cliente:"Constructora XYZ", codigo:"", ciudad: cfg.ciudad, area:"", pisos:"", desc:"" });
    setLogoD(null); setLogoC(null);
  };

  const handleGenerar = async () => {
    setStep(3); setLoading(true); setError(""); setResult(null);
    const msgs = [
      "Configurando plantillas ElectroHub…",
      "Insertando logo diseñador…",
      "Insertando logo cliente…",
      "Creando carpeta en Drive…",
    ];
    let i = 0;
    const iv = setInterval(() => setLoadMsg(msgs[Math.min(++i, msgs.length - 1)]), 800);

    try {
      const res  = await fetch(ENDPOINT, {
        method:"POST",
        body: JSON.stringify({ ...form, tipo, subTipo, disciplinas: discs, modulos, cadTipos, userEmail: user?.email || "", accessToken: user?.accessToken || "" }),
      });
      const data = await res.json();
      clearInterval(iv);
      if (data.ok) {
        setResult(data);
        setHistorial(prev => [{ ...data, nombre:form.nombre, cliente:form.cliente, tipo, fecha: new Date().toLocaleDateString("es-CO") }, ...prev]);
      } else {
        setError(data.error || "Error desconocido");
      }
    } catch {
      clearInterval(iv);
      const demo = {
        ok:true, demo:true,
        codigo: `EH-${new Date().getFullYear()}-${String(historial.length+1).padStart(3,"0")}`,
        archivos: discs.length * 2 + 4,
        url: "#demo",
        carpeta: `EH-001_${(form.cliente||"Cliente").replace(/\s/g,"-")}_${tipo}`,
      };
      setResult(demo);
      setHistorial(prev => [{ ...demo, nombre:form.nombre, cliente:form.cliente, tipo, fecha:new Date().toLocaleDateString("es-CO") }, ...prev]);
    }
    setLoading(false);
  };

  // ── LAYOUT ──
  if (!user) return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <LoginScreen onLogin={setUser} />
    </GoogleOAuthProvider>
  );

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <div style={{ display:"flex", minHeight:"100vh", fontFamily:"'DM Sans', system-ui, sans-serif", background:"#F4F5F7" }}>

      {/* SIDEBAR */}
      <aside style={{ width:220, background:C.blue, display:"flex", flexDirection:"column", padding:"28px 18px", position:"sticky", top:0, height:"100vh", flexShrink:0 }}>
        <div style={{ marginBottom:32 }}>
          <BoltLogo size={44} white />
          <div style={{ color:"#fff", fontWeight:700, fontSize:15, marginTop:10, letterSpacing:-.3 }}>
            Electro<span style={{ color:"#FF9A4D" }}>Hub</span>
          </div>
          <div style={{ color:"rgba(255,255,255,.4)", fontSize:10, marginTop:2, letterSpacing:.6, textTransform:"uppercase" }}>
            ProjectFiles
          </div>
        </div>

        <nav style={{ display:"flex", flexDirection:"column", gap:4, flex:1 }}>
          {[
            ["nuevo",        "⚡ Nuevo proyecto"],
            ["historial",    "☰ Historial"],
            ["calculador",   "📊 Conductor económico"],
            ["memc",         "📋 MEMC"],
            ["config",       "⚙️ Configuración"],
            null,
            ["coord_prot",   "🛡️ Coord. protecciones"],
            ["calidad_pot",  "📈 Calidad de la Potencia"],
            ["arranque",     "⚙️ Arranque de motores"],
            ["flujos",       "🔀 Flujos de Carga"],
            ["cortocircuito","⚡ Cortocircuito"],
          ].map((item, idx) => {
            if (item === null) return (
              <div key={`sep-${idx}`} style={{ height:1, background:"rgba(255,255,255,.08)", margin:"6px 0" }} />
            );
            const [v, l] = item;
            const bloqueado = !allowed.includes(v);
            return (
              <button key={v} onClick={() => bloqueado ? setShowUpgrade(true) : setVista(v)} style={{
                background: vista===v ? "rgba(255,107,0,.15)" : "none",
                border:"none",
                color: bloqueado ? "rgba(255,255,255,.25)" : vista===v ? "#FF9A4D" : "rgba(255,255,255,.45)",
                fontSize:13, fontWeight: vista===v ? 600 : 400,
                padding:"9px 12px", borderRadius:7,
                cursor: "pointer",
                textAlign:"left", transition:"all .15s", fontFamily:"inherit",
                display:"flex", justifyContent:"space-between", alignItems:"center",
              }}>
                <span>{l}</span>
                {bloqueado && <span style={{ fontSize:10, background:"rgba(255,107,0,.25)", color:"#FF9A4D", padding:"2px 6px", borderRadius:4, fontWeight:600 }}>PRO</span>}
              </button>
            );
          })}
        </nav>

        {/* Usuario */}
        <div style={{ marginTop:"auto", paddingTop:16, borderTop:"1px solid rgba(255,255,255,.1)" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
            {user.picture
              ? <img src={user.picture} alt="" style={{ width:28, height:28, borderRadius:"50%", border:"2px solid rgba(255,255,255,.2)" }} />
              : <div style={{ width:28, height:28, borderRadius:"50%", background:"rgba(255,107,0,.4)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, color:"#fff", fontWeight:700 }}>{user.nombre?.[0]?.toUpperCase()}</div>
            }
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ color:"#fff", fontSize:11, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{user.nombre}</div>
              <div style={{ fontSize:10, color: plan==="pro" ? "#FF9A4D" : "rgba(255,255,255,.35)", textTransform:"uppercase", letterSpacing:.5 }}>{plan === "pro" ? "⭐ Pro" : "Free"}</div>
              {plan !== "pro" && (
                <button onClick={() => setShowUpgrade(true)} style={{
                  marginTop:4, fontSize:10, fontWeight:700, color:"#FF6B00",
                  background:"rgba(255,107,0,.15)", border:"1px solid rgba(255,107,0,.3)",
                  borderRadius:4, padding:"2px 7px", cursor:"pointer", fontFamily:"inherit",
                }}>⭐ Actualizar a Pro</button>
              )}
            </div>
          </div>
          <button onClick={handleLogout} style={{ width:"100%", background:"rgba(255,255,255,.07)", border:"none", color:"rgba(255,255,255,.45)", fontSize:11, padding:"6px 10px", borderRadius:6, cursor:"pointer", textAlign:"left", fontFamily:"inherit" }}>
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* MAIN */}
      <main style={{ flex:1, padding:"32px 40px", maxWidth: vista === "memc" ? 1080 : 720, margin:"0 auto", width:"100%" }}>
        {vista === "calculador" ? (
          <CalculadorConductor />
        ) : vista === "memc" ? (
          <MEMCApp />
        ) : vista === "historial" ? (
          <Historial historial={historial} user={user} />
        ) : vista === "config" ? (
          <Configuracion cfg={cfg} setCfg={setCfg} onReset={() => setCfg(CFG_DEFAULT)} />
        ) : ["coord_prot","calidad_pot","arranque","flujos","cortocircuito"].includes(vista) ? (
          <ProximamenteView vista={vista} />
        ) : (
          <>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
              <div>
                <h1 style={{ fontSize:20, fontWeight:700, letterSpacing:-.4, margin:0 }}>
                  {step < 3 ? "Nuevo proyecto" : result ? "Paquete listo ⚡" : "Generando…"}
                </h1>
                {step < 3 && <p style={{ fontSize:12, color:"#888", margin:"4px 0 0" }}>Completa los 3 pasos para generar tus plantillas eléctricas</p>}
              </div>
              {step > 0 && !result && (
                <button onClick={reset} style={S.btnOutline}>← Reiniciar</button>
              )}
            </div>

            <Stepper step={step} />

            {/* ── PASO 0: TIPO ── */}
            {step === 0 && (
              <Card>
                <AIHint text={HINTS[tipo] || HINTS[""]} />
                <SectionLabel>¿Qué tipo de proyecto?</SectionLabel>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:10, marginBottom:20 }}>
                  {TIPOS.map(t => (
                    <div key={t.id} onClick={() => { setTipo(t.id); setSubTipo(""); }} style={{
                      border: `1.5px solid ${tipo===t.id ? C.electric : "#E8E8E8"}`,
                      borderRadius:10, padding:"13px 14px", cursor:"pointer",
                      background: tipo===t.id ? "rgba(30,136,229,.04)" : "#fff",
                      boxShadow: tipo===t.id ? `0 0 0 3px rgba(30,136,229,.08)` : "none",
                      transition:"all .16s",
                    }}>
                      <div style={{ fontSize:22, marginBottom:8 }}>{t.icon}</div>
                      <div style={{ fontWeight:700, fontSize:12, marginBottom:2 }}>{t.label}</div>
                      <div style={{ fontSize:10, color:"#999" }}>{t.desc}</div>
                    </div>
                  ))}
                </div>

                {/* SUBTIPO RESIDENCIAL */}
                {tipo === "residencial" && (
                  <div style={{ marginBottom:20 }}>
                    <SectionLabel>Tipo de memoria a generar</SectionLabel>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                      {[
                        { id:"unifamiliar",            icon:"🏡", label:"Unifamiliar básica",              desc:"< 7 kVA · Memoria básica · RETIE 2024" },
                        { id:"multifamiliar_basico",   icon:"🏘️", label:"Multifamiliar básica",            desc:"7–15 kVA · Memoria básica · RETIE 2024" },
                        { id:"multifamiliar_detallado",icon:"🏢", label:"Multifamiliar detallada",          desc:"> 15 kVA · Memoria detallada · RETIE 2024" },
                        { id:"aerea_rural",            icon:"🌿", label:"Red aérea rural",                 desc:"Diseño básico aéreo · RETIE 2024" },
                      ].map(s => (
                        <div key={s.id} onClick={() => setSubTipo(s.id)} style={{
                          border:`1.5px solid ${subTipo===s.id ? C.electric : "rgba(30,136,229,.2)"}`,
                          borderRadius:10, padding:"13px 14px", cursor:"pointer",
                          background: subTipo===s.id ? "rgba(30,136,229,.12)" : "rgba(30,136,229,.04)",
                          boxShadow: subTipo===s.id ? `0 0 0 3px rgba(30,136,229,.15)` : "none",
                          transition:"all .16s",
                        }}>
                          <div style={{ fontSize:22, marginBottom:8 }}>{s.icon}</div>
                          <div style={{ fontWeight:700, fontSize:12, marginBottom:2, color: subTipo===s.id ? C.electric : "#1a5c9e" }}>{s.label}</div>
                          <div style={{ fontSize:10, color: subTipo===s.id ? "rgba(30,136,229,.7)" : "#6a9cc5" }}>{s.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <SectionLabel>Vista previa — membrete ElectroHub</SectionLabel>
                <MembretePreview logoD={logoD} logoC={logoC} cliente={form.cliente} cfg={cfg} />

                <NavRow>
                  <span/>
                  <button
                    disabled={!tipo || (tipo === "residencial" && !subTipo)}
                    onClick={() => setStep(1)}
                    style={{ ...S.btnPrimary, opacity: (tipo && (tipo !== "residencial" || subTipo)) ? 1 : .35 }}>
                    Continuar →
                  </button>
                </NavRow>
              </Card>
            )}

            {/* ── PASO 1: DATOS Y LOGOS ── */}
            {step === 1 && (
              <Card>
                <AIHint text={`${HINTS[tipo]} Con estos datos preconfiguraremos membrete y metadatos en todos los archivos.`} />
                <Field label="Nombre del proyecto" value={form.nombre}
                  onChange={v => setForm({...form,nombre:v})}
                  placeholder="Ej. Centro Comercial Portal Norte" />
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                  <Field label="Cliente / Contratante" value={form.cliente}
                    onChange={v => setForm({...form,cliente:v})}
                    placeholder="Ej. Grupo Inmobiliario XYZ" />
                  <Field label="Código (opcional)" value={form.codigo}
                    onChange={v => setForm({...form,codigo:v})}
                    placeholder="Auto: EH-2025-001" />
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
                  <Field label="Municipio"  value={form.ciudad} onChange={v=>setForm({...form,ciudad:v})}  placeholder="Bogotá" />
                  <Field label="Área (m²)"  value={form.area}   onChange={v=>setForm({...form,area:v})}    placeholder="5.000" />
                  <Field label="Pisos"       value={form.pisos}  onChange={v=>setForm({...form,pisos:v})}   placeholder="12" />
                </div>
                <Field label="Descripción del proyecto" value={form.desc}
                  onChange={v => setForm({...form,desc:v})}
                  placeholder="Sistema MT, subestación 500 kVA, RETIE 2023, alumbrado RETILAP…"
                  multiline />

                {/* LOGOS */}
                <div style={{ background:"#F8F8F8", border:"0.5px solid #E8E8E8", borderRadius:10, padding:14, marginBottom:16 }}>
                  <div style={{ fontSize:11, fontWeight:700, marginBottom:10, display:"flex", alignItems:"center", gap:6 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.electric} strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                    Logos para membrete y plantillas
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                    <LogoUpload label="Logo empresa diseñadora" sub="ElectroHub · tu empresa"
                      icon="⚡" logo={logoD} onLogo={setLogoD} onRemove={() => setLogoD(null)} />
                    <LogoUpload label="Logo del cliente" sub="Empresa contratante"
                      icon="🏢" logo={logoC} onLogo={setLogoC} onRemove={() => setLogoC(null)} />
                  </div>
                  <div style={{ fontSize:10, color:"#aaa", marginTop:8, lineHeight:1.5 }}>
                    Los logos se insertan en el encabezado de todos los documentos Word, Excel y PDF generados.
                  </div>
                </div>

                <NavRow>
                  <button onClick={() => setStep(0)} style={S.btnOutline}>← Atrás</button>
                  <button disabled={!form.nombre || !form.cliente} onClick={() => setStep(2)}
                    style={{ ...S.btnPrimary, opacity:(form.nombre&&form.cliente)?1:.35 }}>Siguiente →</button>
                </NavRow>
              </Card>
            )}

            {/* ── PASO 2: DISCIPLINAS ── */}
            {step === 2 && (
              <Card>
                <AIHint text={`Selecciona los módulos de entrega y disciplinas para "${form.nombre}". Solo disciplinas eléctricas disponibles en ElectroHub.`} />

                {/* MÓDULOS DE ENTREGA */}
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontSize:11, fontWeight:700, letterSpacing:.6, textTransform:"uppercase", color:"#555", marginBottom:10, display:"flex", alignItems:"center", gap:8 }}>
                    <span>Módulos de entrega</span>
                    <div style={{ flex:1, height:"0.5px", background:"#E8E8E8" }}/>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:8 }}>
                    {MODULOS.map(m => {
                      const sel = modulos.includes(m.id);
                      const operador = m.id === "planos_or" ? getOperador(form.ciudad) : null;
                      const desc = m.id === "planos_or"
                        ? (operador ? `OR: ${operador}` : "Ingresa la ciudad para detectar el operador")
                        : m.desc;
                      const descColor = m.id === "planos_or" && operador ? C.orange : "#999";
                      return (
                        <div key={m.id} onClick={() => toggleModulo(m.id)} style={{
                          border:`1.5px solid ${sel ? m.color : "#E8E8E8"}`,
                          borderRadius:10, padding:"12px 14px", cursor:"pointer",
                          background: sel ? `${m.color}0D` : "#fff",
                          boxShadow: sel ? `0 0 0 3px ${m.color}18` : "none",
                          transition:"all .16s", display:"flex", alignItems:"flex-start", gap:10,
                        }}>
                          <div style={{ fontSize:20, lineHeight:1, marginTop:1 }}>{m.icon}</div>
                          <div>
                            <div style={{ fontSize:12, fontWeight:700, color: sel ? m.color : "#333", marginBottom:2 }}>{m.label}</div>
                            <div style={{ fontSize:10, color: sel && m.id==="planos_or" && operador ? m.color : descColor, lineHeight:1.4, fontWeight: operador && m.id==="planos_or" ? 600 : 400 }}>{desc}</div>
                          </div>
                          <div style={{
                            marginLeft:"auto", width:16, height:16, borderRadius:"50%", flexShrink:0,
                            border:`1.5px solid ${sel ? m.color : "#DDD"}`,
                            background: sel ? m.color : "transparent",
                            display:"grid", placeItems:"center",
                          }}>
                            {sel && <svg width="8" height="8" viewBox="0 0 10 10"><polyline points="2,5 4,7 8,3" stroke="#fff" strokeWidth="1.5" fill="none" strokeLinecap="round"/></svg>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* SUB-SELECCIÓN PLANOS DE USO FINAL */}
                {modulos.includes("planos_uso_final") && (
                  <div style={{ marginBottom:20, background:"rgba(30,136,229,.03)", border:"1px solid rgba(30,136,229,.15)", borderRadius:10, padding:"14px 16px" }}>
                    <div style={{ fontSize:11, fontWeight:700, color:C.electric, marginBottom:10, display:"flex", alignItems:"center", gap:6 }}>
                      <span>📋</span> Planos de Uso Final — selecciona el formato
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                      {[
                        { id:"autocad", icon:"📐", label:"Plantilla AutoCAD", desc:"Convenciones · bloques · capas DWG" },
                        { id:"revit",   icon:"🧱", label:"Plantilla REVIT",   desc:"Familias · vistas · parámetros RVT" },
                      ].map(c => {
                        const sel = cadTipos.includes(c.id);
                        return (
                          <div key={c.id} onClick={() => toggleCadTipo(c.id)} style={{
                            border:`1.5px solid ${sel ? C.electric : "rgba(30,136,229,.2)"}`,
                            borderRadius:8, padding:"11px 13px", cursor:"pointer",
                            background: sel ? "rgba(30,136,229,.1)" : "rgba(30,136,229,.03)",
                            boxShadow: sel ? `0 0 0 3px rgba(30,136,229,.12)` : "none",
                            transition:"all .15s", display:"flex", alignItems:"center", gap:10,
                          }}>
                            <div style={{ fontSize:20 }}>{c.icon}</div>
                            <div>
                              <div style={{ fontSize:12, fontWeight:700, color: sel ? C.electric : "#1a5c9e", marginBottom:2 }}>{c.label}</div>
                              <div style={{ fontSize:10, color: sel ? "rgba(30,136,229,.7)" : "#6a9cc5" }}>{c.desc}</div>
                            </div>
                            <div style={{
                              marginLeft:"auto", width:16, height:16, borderRadius:"50%", flexShrink:0,
                              border:`1.5px solid ${sel ? C.electric : "#DDD"}`,
                              background: sel ? C.electric : "transparent",
                              display:"grid", placeItems:"center",
                            }}>
                              {sel && <svg width="8" height="8" viewBox="0 0 10 10"><polyline points="2,5 4,7 8,3" stroke="#fff" strokeWidth="1.5" fill="none" strokeLinecap="round"/></svg>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* DISCIPLINAS */}
                <div style={{ fontSize:11, fontWeight:700, letterSpacing:.6, textTransform:"uppercase", color:"#555", marginBottom:10, display:"flex", alignItems:"center", gap:8 }}>
                  <span>Disciplinas</span>
                  <div style={{ flex:1, height:"0.5px", background:"#E8E8E8" }}/>
                </div>

                {/* ACTIVAS */}
                {Object.entries(DISC_ACTIVAS).map(([key, disc]) => (
                  <div key={key} style={{ marginBottom:14, borderRadius:10, overflow:"hidden" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 12px", background:"#fff", border:"0.5px solid #E8E8E8", borderRadius:"8px 8px 0 0" }}>
                      <div style={{ width:8, height:8, borderRadius:"50%", background:disc.color, flexShrink:0 }}/>
                      <span style={{ fontWeight:600, fontSize:12 }}>{disc.label}</span>
                      <span style={{ fontSize:10, color:"#aaa", marginLeft:4 }}>{disc.sub}</span>
                    </div>
                    <div style={{ padding:"10px 12px", background:"#fff", border:"0.5px solid #E8E8E8", borderTop:"none", borderRadius:"0 0 8px 8px" }}>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                        {disc.items.map(item => (
                          <button key={item} onClick={() => toggleDisc(item)} style={{
                            fontSize:11, padding:"4px 10px", borderRadius:20, cursor:"pointer", fontFamily:"inherit", fontWeight:500,
                            border: discs.includes(item) ? `0.5px solid ${disc.color}` : "0.5px solid #E0E0E0",
                            background: discs.includes(item) ? `${disc.color}18` : "#fff",
                            color: discs.includes(item) ? disc.color : "#888",
                            transition:"all .14s",
                          }}>{item}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}

                {/* DESHABILITADAS */}
                <div style={{ fontSize:10, fontWeight:700, letterSpacing:.6, textTransform:"uppercase", color:"#ccc", margin:"16px 0 8px", display:"flex", alignItems:"center", gap:8 }}>
                  <span>No disponible en ElectroHub</span>
                  <div style={{ flex:1, height:"0.5px", background:"#EEE" }}/>
                </div>

                {DISC_DESHABILITADAS.map((disc, i) => (
                  <div key={i} style={{ marginBottom:10, borderRadius:10, overflow:"hidden", opacity:.4, pointerEvents:"none" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 12px", background:"#F8F8F8", border:"0.5px solid #EEE", borderRadius:"8px 8px 0 0" }}>
                      <div style={{ width:8, height:8, borderRadius:"50%", background:"#CCC" }}/>
                      <span style={{ fontWeight:600, fontSize:12, color:"#AAA" }}>{disc.label}</span>
                      <span style={{ marginLeft:"auto", fontSize:9, fontWeight:700, padding:"1px 7px", borderRadius:20, border:"0.5px solid #DDD", color:"#BBB", letterSpacing:.4 }}>No disponible</span>
                    </div>
                    <div style={{ padding:"10px 12px", background:"#F8F8F8", border:"0.5px solid #EEE", borderTop:"none", borderRadius:"0 0 8px 8px" }}>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                        {disc.items.map(item => (
                          <span key={item} style={{ fontSize:11, padding:"4px 10px", borderRadius:20, border:"0.5px solid #E8E8E8", background:"#F5F5F5", color:"#CCC" }}>{item}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}

                <NavRow>
                  <button onClick={() => setStep(1)} style={S.btnOutline}>← Atrás</button>
                  <button onClick={handleGenerar} style={S.btnPrimary}>Generar plantillas ⚡</button>
                </NavRow>
              </Card>
            )}

            {/* ── PASO 3: RESULTADO ── */}
            {step === 3 && (
              <Card>
                {loading && (
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"center", padding:"48px 0", gap:12 }}>
                    <div style={{ width:32, height:32, border:`3px solid #EEE`, borderTopColor:C.electric, borderRadius:"50%", animation:"spin .7s linear infinite" }}/>
                    <div style={{ fontSize:13, color:"#888" }}>{loadMsg}</div>
                    <div style={{ fontSize:11, color:"#bbb" }}>Aplicando logos ElectroHub a cada archivo…</div>
                  </div>
                )}

                {error && (
                  <div style={{ background:"#FFF5F5", border:"1px solid #FCC", borderRadius:10, padding:20, color:"#CC2222" }}>
                    <div style={{ fontWeight:600, marginBottom:4 }}>Error al generar</div>
                    <div style={{ fontSize:12 }}>{error}</div>
                    <button onClick={() => setStep(2)} style={{ ...S.btnOutline, marginTop:12 }}>← Reintentar</button>
                  </div>
                )}

                {result && !loading && (
                  <>
                    {result.demo && (
                      <div style={{ background:"#FFFBE6", border:"1px solid rgba(255,107,0,.3)", borderRadius:8, padding:"9px 14px", fontSize:11, color:"#8A5000", marginBottom:16 }}>
                        Modo demo — conecta tu endpoint en <code>.env.local</code> → <code>REACT_APP_ENDPOINT</code>
                      </div>
                    )}

                    <AIHint text={`¡Listo! ${result.archivos} plantillas generadas para "${form.nombre}" (${result.codigo}). Logos de ElectroHub y "${form.cliente}" aplicados en todos los archivos.`} />

                    {/* Logos en resultado */}
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:16 }}>
                      {[{logo:logoD,label:"Empresa diseñadora",fallback:"ElectroHub"},{logo:logoC,label:"Cliente",fallback:form.cliente}].map((l,i) => (
                        <div key={i} style={{ border:"0.5px solid #EEE", borderRadius:8, padding:12, background:"#fff", textAlign:"center" }}>
                          {l.logo
                            ? <img src={l.logo.url} alt="" style={{ maxHeight:36, maxWidth:"100%", objectFit:"contain", display:"block", margin:"0 auto 6px" }}/>
                            : <div style={{ width:50, height:32, background:"#F5F5F5", borderRadius:4, margin:"0 auto 6px", display:"grid", placeItems:"center", fontSize:16 }}>🖼️</div>
                          }
                          <div style={{ fontSize:10, color:"#aaa" }}>{l.label}</div>
                          <div style={{ fontSize:11, fontWeight:600, marginTop:1 }}>{l.logo ? l.logo.name : l.fallback}</div>
                        </div>
                      ))}
                    </div>

                    {/* Stats */}
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:16 }}>
                      {[
                        {n:result.archivos, l:"Archivos", c:C.blue},
                        {n:discs.length||"–", l:"Disciplinas", c:C.electric},
                        {n:result.codigo, l:"Código EH", c:C.orange, sm:true},
                      ].map((s,i) => (
                        <div key={i} style={{ background:"#F8F8F8", borderRadius:8, padding:"12px 10px", textAlign:"center" }}>
                          <div style={{ fontSize:s.sm?13:22, fontWeight:700, color:s.c, marginBottom:2 }}>{s.n}</div>
                          <div style={{ fontSize:10, color:"#aaa" }}>{s.l}</div>
                        </div>
                      ))}
                    </div>

                    {/* Drive link */}
                    <div style={{ display:"flex", alignItems:"center", gap:12, background:"rgba(52,168,83,.05)", border:"1px solid rgba(52,168,83,.2)", borderRadius:10, padding:"13px 16px", marginBottom:10 }}>
                      <svg width="22" height="19" viewBox="0 0 87.3 78" fill="none" style={{flexShrink:0}}>
                        <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H.1c0 1.55.4 3.1 1.2 4.5z" fill="#0066DA"/>
                        <path d="M43.65 25L29.9 1.2C28.55 2 27.4 3.1 26.6 4.5L1.3 48.4a9 9 0 0 0-1.2 4.45h27.5z" fill="#00AC47"/>
                        <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.8l5.85 10.6z" fill="#EA4335"/>
                        <path d="M43.65 25L57.4 1.2a9 9 0 0 0-4.5-1.2H34.4c-1.55 0-3.1.45-4.5 1.2z" fill="#00832D"/>
                        <path d="M59.8 52.85H27.5L13.75 76.65c1.4.8 2.95 1.2 4.5 1.2h50.8c1.55 0 3.1-.4 4.5-1.2z" fill="#2684FC"/>
                        <path d="M73.4 26.45l-12.65-21.9c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.15 27.85h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#FFBA00"/>
                      </svg>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:600,fontSize:12,marginBottom:2}}>Carpeta ElectroHub creada en Google Drive</div>
                        <div style={{fontSize:11,color:"#888",fontFamily:"'DM Mono',monospace"}}>{result.carpeta}</div>
                      </div>
                      {result.url !== "#demo" && (
                        <a href={result.url} target="_blank" rel="noreferrer" style={S.btnPrimary}>Abrir →</a>
                      )}
                    </div>

                    <button onClick={reset} style={{ ...S.btnOutline, width:"100%", marginTop:4 }}>
                      ← Nuevo proyecto ElectroHub
                    </button>
                  </>
                )}
              </Card>
            )}
          </>
        )}
      </main>
    </div>
    {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}
    </GoogleOAuthProvider>
  );
}

// ── CONFIGURACIÓN ─────────────────────────────────────────────
function Configuracion({ cfg, setCfg, onReset }) {
  const set = (k, v) => setCfg(prev => ({ ...prev, [k]: v }));
  const F = ({ label, k, placeholder }) => (
    <div style={{ marginBottom:14 }}>
      <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#777", marginBottom:5 }}>{label}</label>
      <input value={cfg[k]} onChange={e => set(k, e.target.value)} placeholder={placeholder}
        style={{ width:"100%", padding:"9px 12px", border:"0.5px solid #E0E0E0", borderRadius:7,
          fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }} />
    </div>
  );
  return (
    <div style={{ paddingTop:24 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <div>
          <h2 style={{ fontSize:18, fontWeight:700, margin:0 }}>Configuración</h2>
          <p style={{ fontSize:12, color:"#888", margin:"4px 0 0" }}>Datos del diseñador y apariencia del membrete</p>
        </div>
        <button onClick={onReset} style={{ fontSize:11, color:"#CC2222", background:"none", border:"1px solid #FFCDD2", borderRadius:7, padding:"6px 14px", cursor:"pointer", fontFamily:"inherit" }}>
          Restablecer valores
        </button>
      </div>

      <div style={{ background:"#fff", borderRadius:12, padding:28, boxShadow:"0 1px 6px rgba(0,0,0,.05)", marginBottom:16 }}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:.6, textTransform:"uppercase", color:"#555", marginBottom:16, display:"flex", alignItems:"center", gap:8 }}>
          <span>Datos de la empresa</span><div style={{ flex:1, height:"0.5px", background:"#E8E8E8" }}/>
        </div>
        <F label="Nombre empresa / diseñadora" k="empresa" placeholder="ElectroHub Ingeniería S.A.S." />
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <F label="Firmante" k="firmante" placeholder="Ing. Juan David Marín" />
          <F label="Título profesional" k="titulo" placeholder="Ingeniero Electricista · RETIE" />
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <F label="Correo electrónico" k="email" placeholder="proyectos@electrohub.app" />
          <F label="Sitio web" k="web" placeholder="www.electrohub.app" />
        </div>
        <F label="Ciudad principal" k="ciudad" placeholder="Bogotá" />
      </div>

      <div style={{ background:"#fff", borderRadius:12, padding:28, boxShadow:"0 1px 6px rgba(0,0,0,.05)", marginBottom:16 }}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:.6, textTransform:"uppercase", color:"#555", marginBottom:16, display:"flex", alignItems:"center", gap:8 }}>
          <span>Colores del membrete</span><div style={{ flex:1, height:"0.5px", background:"#E8E8E8" }}/>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
          {[["colorPrimario","Color primario (fondo encabezado)"],["colorAcento","Color acento (línea inferior)"]].map(([k, label]) => (
            <div key={k}>
              <label style={{ display:"block", fontSize:11, fontWeight:600, color:"#777", marginBottom:8 }}>{label}</label>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <input type="color" value={cfg[k]} onChange={e => set(k, e.target.value)}
                  style={{ width:44, height:44, borderRadius:8, border:"0.5px solid #E0E0E0", cursor:"pointer", padding:2 }} />
                <div>
                  <div style={{ width:80, height:24, borderRadius:6, background:cfg[k], marginBottom:4, border:"0.5px solid #E0E0E0" }}/>
                  <div style={{ fontSize:11, fontFamily:"monospace", color:"#888" }}>{cfg[k]}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background:"#fff", borderRadius:12, padding:28, boxShadow:"0 1px 6px rgba(0,0,0,.05)" }}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:.6, textTransform:"uppercase", color:"#555", marginBottom:16, display:"flex", alignItems:"center", gap:8 }}>
          <span>Vista previa membrete</span><div style={{ flex:1, height:"0.5px", background:"#E8E8E8" }}/>
        </div>
        <MembretePreview cfg={cfg} />
      </div>
    </div>
  );
}

// ── HISTORIAL ──────────────────────────────────────────────────
const TIPO_COLORS = {
  comercial:      { bg:"#E3F2FD", fg:"#0D47A1" },
  industrial:     { bg:"#FFF3E0", fg:"#E65100" },
  infraestructura:{ bg:"#F3E5F5", fg:"#6A1B9A" },
  residencial:    { bg:"#E8F5E9", fg:"#1B5E20" },
};

function Historial({ historial: local, user }) {
  const [proyectos, setProyectos] = useState([]);
  const [cargando, setCargando]   = useState(true);
  const [error, setError]         = useState("");

  useEffect(() => {
    async function cargar() {
      try {
        const url = `${process.env.REACT_APP_ENDPOINT}?accion=proyectos&email=${encodeURIComponent(user?.email || "")}`;
        const res  = await fetch(url);
        const data = await res.json();
        if (data.proyectos) {
          const userEmail = (user?.email || "").toLowerCase();
          const remotos = data.proyectos
            .filter(p => {
              const pEmail = String(p["Email"] || p["email"] || p["Usuario"] || "").toLowerCase().trim();
              return pEmail !== "" && pEmail === userEmail;
            })
            .map(p => ({
              nombre:  p["Nombre proyecto"] || p["Nombre"] || p[Object.keys(p)[2]] || "—",
              cliente: p["Cliente"]  || p[Object.keys(p)[3]] || "—",
              tipo:    String(p["Tipo"] || p[Object.keys(p)[4]] || "").toLowerCase(),
              codigo:  p["Código"]   || p["Codigo"] || p[Object.keys(p)[1]] || "—",
              archivos:p["N° Archivos"] || p["Archivos"] || p[Object.keys(p)[11]] || "—",
              url:     p["URL"]      || p[Object.keys(p)[12]] || "",
              fecha:   p["Fecha"]    ? new Date(p["Fecha"]).toLocaleDateString("es-CO") : "—",
              remoto: true,
            }));
          setProyectos(remotos);
        }
      } catch(e) { setError("No se pudo cargar el historial del servidor."); }
      finally { setCargando(false); }
    }
    cargar();
  }, [user?.email]);

  const todos = [
    ...local.filter(p => !proyectos.find(r => r.codigo === p.codigo)),
    ...proyectos,
  ];

  const tipoColor = t => TIPO_COLORS[t] || { bg:"#F5F5F5", fg:"#555" };

  if (cargando) return (
    <div style={{ padding:48, textAlign:"center", color:"#aaa" }}>
      <div style={{ fontSize:28, marginBottom:12 }}>⏳</div>
      <div style={{ fontSize:13 }}>Cargando proyectos…</div>
    </div>
  );

  return (
    <div style={{ paddingTop:24 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <h2 style={{ fontSize:18, fontWeight:700, margin:0 }}>Historial de proyectos</h2>
        <span style={{ fontSize:12, color:"#aaa" }}>{todos.length} proyecto{todos.length !== 1 ? "s" : ""}</span>
      </div>

      {error && <div style={{ background:"#FEF3C7", border:"1px solid #FCD34D", borderRadius:8, padding:"10px 14px", fontSize:12, color:"#92400E", marginBottom:16 }}>{error}</div>}

      {!todos.length ? (
        <div style={{ padding:48, textAlign:"center", color:"#aaa" }}>
          <div style={{ fontSize:36, marginBottom:12 }}>⚡</div>
          <div style={{ fontSize:14 }}>Aún no has generado proyectos.</div>
        </div>
      ) : todos.map((p, i) => {
        const { bg, fg } = tipoColor(p.tipo);
        return (
          <div key={i} style={{ background:"#fff", border:"1px solid #EEE", borderRadius:10, padding:"15px 18px", marginBottom:10, boxShadow:"0 1px 4px rgba(0,0,0,.04)" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8 }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:600, fontSize:13, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.nombre}</div>
                <div style={{ fontSize:11, color:"#888", marginTop:2 }}>{p.cliente}</div>
              </div>
              <span style={{ fontSize:10, fontWeight:700, padding:"3px 9px", borderRadius:20, textTransform:"uppercase", letterSpacing:.4, background:bg, color:fg, flexShrink:0 }}>
                {p.tipo || "—"}
              </span>
            </div>
            <div style={{ display:"flex", gap:16, marginTop:10, fontSize:11, color:"#aaa", flexWrap:"wrap" }}>
              <span>⚡ {p.codigo}</span>
              <span>📄 {p.archivos} archivos</span>
              <span>📅 {p.fecha}</span>
            </div>
            {p.url && p.url !== "#demo" && (
              <a href={p.url} target="_blank" rel="noreferrer" style={{ fontSize:11, color:C.orange, fontWeight:600, marginTop:8, display:"inline-block" }}>
                Abrir en Drive →
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── HELPERS ───────────────────────────────────────────────────
const Card = ({children}) => (
  <div style={{ background:"#fff", borderRadius:12, padding:28, boxShadow:"0 1px 6px rgba(0,0,0,.05)" }}>{children}</div>
);
const SectionLabel = ({children}) => (
  <div style={{ fontSize:10, fontWeight:700, letterSpacing:.7, textTransform:"uppercase", color:"#aaa", marginBottom:12 }}>{children}</div>
);
const NavRow = ({children}) => (
  <div style={{ display:"flex", justifyContent:"space-between", marginTop:20 }}>{children}</div>
);

const S = {
  btnPrimary: {
    padding:"9px 20px", background:C.blue, color:"#fff",
    border:"none", borderRadius:7, fontSize:12, fontWeight:600,
    cursor:"pointer", fontFamily:"inherit", textDecoration:"none",
    display:"inline-flex", alignItems:"center",
  },
  btnOutline: {
    padding:"9px 20px", background:"none", color:"#111",
    border:"1.5px solid #DDD", borderRadius:7, fontSize:12,
    fontWeight:600, cursor:"pointer", fontFamily:"inherit",
  },
};

// ══════════════════════════════════════════════════════════════
// CALCULADOR CONDUCTOR ECONÓMICO — GM-01 / GTC 221 / RETIE 2024
// ══════════════════════════════════════════════════════════════
const CALIBRES_CALC = [
  { name:"#14 AWG", area:2.08,  amp75:15,  r:8.450, awg:"14"  },
  { name:"#12 AWG", area:3.31,  amp75:20,  r:5.310, awg:"12"  },
  { name:"#10 AWG", area:5.26,  amp75:30,  r:3.350, awg:"10"  },
  { name:"#8 AWG",  area:8.37,  amp75:40,  r:2.110, awg:"8"   },
  { name:"#6 AWG",  area:13.30, amp75:55,  r:1.320, awg:"6"   },
  { name:"#4 AWG",  area:21.15, amp75:70,  r:0.836, awg:"4"   },
  { name:"#2 AWG",  area:33.62, amp75:95,  r:0.526, awg:"2"   },
  { name:"#1 AWG",  area:42.41, amp75:110, r:0.416, awg:"1"   },
  { name:"#1/0 AWG",area:53.48, amp75:125, r:0.330, awg:"1/0" },
  { name:"#2/0 AWG",area:67.43, amp75:145, r:0.262, awg:"2/0" },
  { name:"#3/0 AWG",  area:85.01,  amp75:165, r:0.208,  awg:"3/0"  },
  { name:"#4/0 AWG",  area:107.2,  amp75:195, r:0.164,  awg:"4/0"  },
  { name:"250 MCM",   area:126.7,  amp75:215, r:0.1378, awg:"250"  },
  { name:"300 MCM",   area:152.0,  amp75:240, r:0.1148, awg:"300"  },
  { name:"350 MCM",   area:177.3,  amp75:260, r:0.0983, awg:"350"  },
  { name:"400 MCM",   area:202.7,  amp75:280, r:0.0860, awg:"400"  },
  { name:"500 MCM",   area:253.4,  amp75:320, r:0.0690, awg:"500"  },
];
const K_CALC = { cu_xlpe:143, cu_thw:115, cu_desnudo:143, al_xlpe:94, al_acsr:93, al_pvc:76 };
const TEMP_CORR_CALC = [[25,1.00],[30,0.96],[35,0.92],[40,0.87],[45,0.84],[50,0.79],[55,0.73],[60,0.68]];

function getTempCorrCalc(ta) {
  for (let i = TEMP_CORR_CALC.length-1; i>=0; i--) { if (ta >= TEMP_CORR_CALC[i][0]) return TEMP_CORR_CALC[i][1]; }
  return 1.0;
}
function calcInom(s, vl, tipo) {
  if (tipo==="trifasico") return (s*1000)/(Math.sqrt(3)*vl);
  if (tipo==="monof_trifilar") return (s*1000)/(2*(vl/Math.sqrt(3)));
  return (s*1000)/(vl/Math.sqrt(3));
}
function fmtN(n,d=2) { return (isNaN(n)||!isFinite(n)) ? "—" : Number(n).toFixed(d); }
function fmtCOP(n) { return (isNaN(n)||!isFinite(n)) ? "—" : "$"+Math.round(n).toLocaleString("es-CO"); }

function CalculadorConductor() {
  const [tab, setTab] = useState("params");
  const [sys, setSys] = useState({ tipo:"trifasico", nivel:"BT", vl:208, fc:0.636, thdi:10, fp:0.90, tempAmb:30 });
  const [eco, setEco] = useState({ tasa:12.4, aumCarga:3, aumEnergia:1, costoP:650, costoD:15, horasDia:10, vidaUtil:20 });
  const [tramo, setTramo] = useState({ s:50, long:0.05, material:"cu_thw", icc:3.5, tFalla:0.03, dmg:100 });
  const [costos, setCostos] = useState(Object.fromEntries(CALIBRES_CALC.map(c=>[c.awg,2000000])));
  const [tramos, setTramos] = useState([]);

  // Factores derivados
  const fc=sys.fc, i_d=eco.tasa/100, a=eco.aumCarga/100, b=eco.aumEnergia/100, N=eco.vidaUtil;
  const Fp = 0.7*fc*fc + 0.3*fc;
  const r_f = Math.pow(1+a,2)*(1+b)/(1+i_d);
  let Q_f=0; for(let n=1;n<=N;n++) Q_f+=Math.pow(r_f,n-1);
  const T_f = eco.horasDia*365;

  // Resultados técnicos
  const { vl,tipo,nivel,fp,thdi,tempAmb } = sys;
  const { s,long,material,icc,tFalla,dmg } = tramo;
  const k_mat = K_CALC[material]||115;
  const tempCorr = getTempCorrCalc(tempAmb);
  const Inom = calcInom(s,vl,tipo);
  const margen = nivel==="MT"?1.15:1.25;
  const Idis = Inom*margen;
  const Itotal = Idis*(1+thdi/100);
  const Sicc = (icc*1000*Math.sqrt(tFalla))/k_mat;
  const XL = 0.1736*Math.log10(Math.max(dmg,1)/5);
  const phi = Math.acos(Math.max(0.01,Math.min(1,fp)));

  const tecRows = CALIBRES_CALC.map(c=>{
    const ampC=c.amp75*tempCorr;
    const dv=(s*long*c.r*fp)/(10*vl*vl)*1e6;
    return { c, ampC, okAmp:ampC>=Itotal, okIcc:c.area>=Sicc, dv, okDv:dv<=5 };
  });

  // Resultados económicos
  const P_e=eco.costoP/1e6, D_e=eco.costoD/1e6;
  const F_e = (T_f*P_e+D_e)*Q_f/(1+i_d);
  const I2 = Itotal*Itotal;
  const ecoRows = CALIBRES_CALC.map(c=>{
    const CI=(costos[c.awg]||0)*long;
    const CJ=I2*c.r*long*F_e;
    return { c, CI, CJ, CT:CI+CJ };
  });
  const optIdx = ecoRows.reduce((best,r,i)=>r.CT<ecoRows[best].CT?i:best,0);

  // Tramos calculados
  const tramosCalc = tramos.map(tr=>{
    const in2=calcInom(tr.s,vl,tipo);
    const it2=in2*margen*(1+thdi/100);
    const cal=CALIBRES_CALC.find(c=>c.name===tr.calibre)||CALIBRES_CALC[1];
    const dv2=(tr.s*tr.long*cal.r*fp)/(10*vl*vl)*1e6;
    const CI2=(tr.ciKm||1500000)*tr.long;
    const CJ2=it2*it2*cal.r*tr.long*F_e;
    return {...tr, it2, dv2, okAmp:cal.amp75*tempCorr>=it2, okDv:dv2<=5, CI:CI2, CJ:CJ2, CT:CI2+CJ2};
  });
  const totCI=tramosCalc.reduce((a,r)=>a+r.CI,0);
  const totCJ=tramosCalc.reduce((a,r)=>a+r.CJ,0);
  const totCT=tramosCalc.reduce((a,r)=>a+r.CT,0);

  // ── Estilos locales ─────────────────────────────────────────
  const Lcard  = { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"16px 18px" };
  const Lgrid2 = { display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 };
  const Lgrid3 = { display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 };
  const Lgrid4 = { display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8 };
  const Lfield = (label, inp) => (
    <div style={{marginBottom:10}}>
      <div style={{fontSize:10,fontWeight:700,letterSpacing:".05em",textTransform:"uppercase",color:"#6b7280",marginBottom:4}}>{label}</div>
      {inp}
    </div>
  );
  const Linput = (val, onChange, type="number", step=1, extra={}) => (
    <input type={type} value={val} step={step} onChange={onChange}
      style={{width:"100%",height:34,padding:"0 9px",border:"1.5px solid #e5e7eb",borderRadius:7,
        background:"#fff",color:"#111",fontSize:12,fontFamily:"'Courier New',monospace",...extra.style}}/>
  );
  const Lselect = (val, onChange, opts) => (
    <select value={val} onChange={onChange}
      style={{width:"100%",height:34,padding:"0 9px",border:"1.5px solid #e5e7eb",borderRadius:7,
        background:"#fff",color:"#111",fontSize:12}}>
      {opts.map(([v,l])=><option key={v} value={v}>{l}</option>)}
    </select>
  );
  const Lmetric = (label, val, unit) => (
    <div style={{background:"#f8fafc",borderRadius:8,padding:"10px 12px",border:"1px solid #f1f5f9"}}>
      <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",color:"#6b7280",marginBottom:3}}>{label}</div>
      <div style={{fontSize:20,fontWeight:700,color:"#0A2463",fontFamily:"'Courier New',monospace"}}>{val}</div>
      {unit&&<div style={{fontSize:10,color:"#9ca3af",marginTop:1}}>{unit}</div>}
    </div>
  );
  const Lformula = (txt) => (
    <div style={{background:"#eff6ff",borderLeft:"3px solid #0A2463",borderRadius:"0 7px 7px 0",
      padding:"9px 12px",margin:"8px 0",fontSize:11,fontFamily:"'Courier New',monospace",
      color:"#1e3a8a",lineHeight:1.9,whiteSpace:"pre-wrap"}}>{txt}</div>
  );
  const Lsectitle = (t) => (
    <div style={{fontSize:10,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",
      color:"#0A2463",borderBottom:"2px solid #FF6B00",paddingBottom:4,marginBottom:12,marginTop:16}}>{t}</div>
  );
  const LBadge = ({ok,warn,children}) => {
    const bg=ok?"#dcfce7":warn?"#fef3c7":"#fee2e2";
    const cl=ok?"#15803d":warn?"#b45309":"#991b1b";
    return <span style={{background:bg,color:cl,fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:5}}>{children}</span>;
  };

  const TABS_C=[["params","1 · Parámetros"],["tecnico","2 · Técnico"],["economico","3 · Económico"],["tramos","4 · Tramos"],["resumen","5 · Resumen"]];

  return (
    <div style={{fontFamily:"inherit",color:"#111",maxWidth:900,margin:"0 auto"}}>

      {/* Header */}
      <div style={{background:"#0A2463",borderRadius:10,padding:"16px 20px 0",marginBottom:16,border:"2px solid #FF6B00"}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
          <div style={{width:36,height:36,background:"#FF6B00",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
          </div>
          <div>
            <div style={{color:"#fff",fontSize:15,fontWeight:700,letterSpacing:"-.02em"}}>Calculador de conductor económico</div>
            <div style={{color:"#93c5fd",fontSize:11,marginTop:2}}>Metodología EPM GM-01 · GTC 221 · RETIE 2024 Ítems (j) y (k)</div>
          </div>
        </div>
        <div style={{display:"flex",gap:2,overflowX:"auto"}}>
          {TABS_C.map(([id,label])=>(
            <button key={id} onClick={()=>setTab(id)} style={{
              padding:"9px 14px",border:"none",background:"transparent",
              color:tab===id?"#fff":"rgba(255,255,255,.5)",
              fontSize:12,fontWeight:tab===id?700:400,cursor:"pointer",
              borderBottom:tab===id?"3px solid #FF6B00":"3px solid transparent",
              whiteSpace:"nowrap",fontFamily:"inherit",
            }}>{label}</button>
          ))}
        </div>
      </div>

      {/* ─── TAB PARÁMETROS ─── */}
      {tab==="params" && (
        <div>
          <div style={Lgrid2}>
            <div style={Lcard}>
              {Lsectitle("Sistema eléctrico")}
              {Lfield("Tipo de sistema", Lselect(sys.tipo, e=>setSys(s=>({...s,tipo:e.target.value})), [
                ["trifasico","Trifásico tetrafilar (Ec. 1)"],["monof_trifilar","Monofásico trifilar (Ec. 2)"],["monof_bifilar","Monofásico bifilar (Ec. 3)"]
              ]))}
              {Lfield("Nivel de tensión", Lselect(sys.nivel, e=>setSys(s=>({...s,nivel:e.target.value})), [
                ["BT","BT — margen 25% (NTC 2050 Art. 220)"],["MT","MT — margen 15%"]
              ]))}
              <div style={Lgrid2}>
                {Lfield("Tensión de línea VL (V)", Linput(sys.vl, e=>setSys(s=>({...s,vl:+e.target.value}))))}
                {Lfield("Factor de potencia cosφ", Linput(sys.fp, e=>setSys(s=>({...s,fp:+e.target.value})),undefined,0.01))}
                {Lfield("Factor de carga FC (0-1)", Linput(sys.fc, e=>setSys(s=>({...s,fc:+e.target.value})),undefined,0.001))}
                {Lfield("THDi (%)", Linput(sys.thdi, e=>setSys(s=>({...s,thdi:+e.target.value})),undefined,0.5))}
              </div>
              {Lfield("Temperatura ambiente (°C)", Linput(sys.tempAmb, e=>setSys(s=>({...s,tempAmb:+e.target.value}))))}
            </div>
            <div style={Lcard}>
              {Lsectitle("Parámetros económicos")}
              <div style={Lgrid2}>
                {Lfield("Tasa descuento i (%)", Linput(eco.tasa, e=>setEco(v=>({...v,tasa:+e.target.value})),undefined,0.1))}
                {Lfield("Aumento carga a (%)", Linput(eco.aumCarga, e=>setEco(v=>({...v,aumCarga:+e.target.value})),undefined,0.5))}
                {Lfield("Aumento energía b (%)", Linput(eco.aumEnergia, e=>setEco(v=>({...v,aumEnergia:+e.target.value})),undefined,0.5))}
                {Lfield("Costo energía P ($/kWh)", Linput(eco.costoP, e=>setEco(v=>({...v,costoP:+e.target.value})),undefined,10))}
                {Lfield("Costo adicional D ($/kWh)", Linput(eco.costoD, e=>setEco(v=>({...v,costoD:+e.target.value}))))}
                {Lfield("Horas/día", Linput(eco.horasDia, e=>setEco(v=>({...v,horasDia:+e.target.value})),undefined,0.5))}
              </div>
              {Lfield("Vida útil N (años)", Linput(eco.vidaUtil, e=>setEco(v=>({...v,vidaUtil:+e.target.value}))))}
            </div>
          </div>
          <div style={{...Lcard,marginTop:12}}>
            {Lsectitle("Factores derivados (calculados automáticamente)")}
            <div style={Lgrid4}>
              {Lmetric("Fp = 0,7·FC²+0,3·FC", fmtN(Fp,4))}
              {Lmetric("r = (1+a)²·(1+b)/(1+i)", fmtN(r_f,4))}
              {Lmetric("Q = Σ r^(n-1)", fmtN(Q_f,2))}
              {Lmetric("T = h/día × 365", Math.round(T_f), "h/año")}
            </div>
          </div>
        </div>
      )}

      {/* ─── TAB TÉCNICO ─── */}
      {tab==="tecnico" && (
        <div>
          <div style={Lcard}>
            {Lsectitle("Parámetros del tramo")}
            <div style={Lgrid3}>
              {Lfield("Potencia S (kVA)", Linput(tramo.s, e=>setTramo(t=>({...t,s:+e.target.value})),undefined,0.5))}
              {Lfield("Longitud L (km)", Linput(tramo.long, e=>setTramo(t=>({...t,long:+e.target.value})),undefined,0.001))}
              {Lfield("Material/aislamiento", Lselect(tramo.material, e=>setTramo(t=>({...t,material:e.target.value})), [
                ["cu_xlpe","Cu XLPE — k=143"],["cu_thw","Cu THW/PVC — k=115"],
                ["cu_desnudo","Cu desnudo — k=143"],["al_acsr","Al ACSR — k=93"],
                ["al_xlpe","Al XLPE — k=94"],["al_pvc","Al PVC — k=76"],
              ]))}
              {Lfield("Icc en el punto (kA)", Linput(tramo.icc, e=>setTramo(t=>({...t,icc:+e.target.value})),undefined,0.1))}
              {Lfield("Tiempo despeje t (s)", Linput(tramo.tFalla, e=>setTramo(t=>({...t,tFalla:+e.target.value})),undefined,0.005))}
              {Lfield("DMG conductores (mm)", Linput(tramo.dmg, e=>setTramo(t=>({...t,dmg:+e.target.value})),undefined,5))}
            </div>
          </div>
          <div style={{...Lcard,marginTop:12}}>
            {Lsectitle("Resultados técnicos")}
            <div style={Lgrid3}>
              {Lmetric("I nominal (Ec. 1-3)", fmtN(Inom,1), "A")}
              {Lmetric(`I diseño ×${margen}`, fmtN(Idis,1), "A")}
              {Lmetric("I total con THD (Ec. 6)", fmtN(Itotal,1), "A")}
              {Lmetric("Sección mín. Icc (Ec. 21)", fmtN(Sicc,2), "mm²")}
              {Lmetric("Reactancia X (Ec. 33)", fmtN(XL,4), "Ω/km")}
              {Lmetric("k conductor", k_mat)}
            </div>
            {Lformula(
              `Ec.1-3: I_nom = ${s} kVA / (√3 × ${vl} V) = ${fmtN(Inom,2)} A\n` +
              `Ec.4/5: I_dis = ${fmtN(Inom,2)} × ${margen} = ${fmtN(Idis,2)} A\n` +
              `Ec.6:   I_tot = ${fmtN(Idis,2)} × (1 + ${thdi}/100) = ${fmtN(Itotal,2)} A\n` +
              `Ec.21:  A_min = ${icc} kA × √${tFalla} s / ${k_mat} = ${fmtN(Sicc,2)} mm²`
            )}
          </div>
          <div style={{...Lcard,marginTop:12}}>
            {Lsectitle(`Verificación por calibre — corrección temp. ×${fmtN(tempCorr,2)}`)}
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                <thead>
                  <tr>{["Calibre","Área mm²","Amp. 75°C","Amp. corr.","R Ω/km","ΔV %","Amp≥Itot","Sección≥Icc","Estado"].map(h=>(
                    <th key={h} style={{background:"#0A2463",color:"#fff",padding:"7px 8px",textAlign:"left",fontWeight:600,whiteSpace:"nowrap"}}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {tecRows.map((row,i)=>(
                    <tr key={i} style={{background:row.okAmp&&row.okIcc?"#f0fdf4":i%2===0?"#fafafa":"#fff"}}>
                      <td style={{padding:"6px 8px",fontWeight:700,fontFamily:"'Courier New',monospace"}}>{row.c.name}</td>
                      <td style={{padding:"6px 8px"}}>{row.c.area}</td>
                      <td style={{padding:"6px 8px"}}>{row.c.amp75}A</td>
                      <td style={{padding:"6px 8px"}}>{fmtN(row.ampC,0)}A</td>
                      <td style={{padding:"6px 8px"}}>{row.c.r}</td>
                      <td style={{padding:"6px 8px",color:row.dv>5?"#dc2626":"#15803d"}}>{fmtN(row.dv,2)}%</td>
                      <td style={{padding:"6px 8px"}}><LBadge ok={row.okAmp}>{row.okAmp?"✓":"✗"} {fmtN(row.ampC,0)}</LBadge></td>
                      <td style={{padding:"6px 8px"}}><LBadge ok={row.okIcc}>{row.okIcc?"✓":"✗"} {row.c.area}</LBadge></td>
                      <td style={{padding:"6px 8px"}}><LBadge ok={row.okAmp&&row.okIcc} warn={row.okAmp&&!row.okIcc}>{row.okAmp&&row.okIcc?"Cumple":row.okAmp?"Insuf.Icc":"Insuf.amp."}</LBadge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ─── TAB ECONÓMICO ─── */}
      {tab==="economico" && (
        <div>
          <div style={Lcard}>
            {Lsectitle("Costos de instalación CI ($/km) — editar con cotizaciones reales")}
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
              {CALIBRES_CALC.map(c=>(
                <div key={c.awg} style={{marginBottom:6}}>
                  <div style={{fontSize:10,fontWeight:700,color:"#6b7280",marginBottom:3}}>{c.name}</div>
                  <input type="number" value={costos[c.awg]} step={50000}
                    onChange={e=>setCostos(v=>({...v,[c.awg]:+e.target.value}))}
                    style={{width:"100%",height:32,padding:"0 8px",border:"1.5px solid #e5e7eb",borderRadius:6,background:"#fff",color:"#111",fontSize:11,fontFamily:"'Courier New',monospace"}}/>
                </div>
              ))}
            </div>
          </div>
          <div style={{...Lcard,marginTop:12}}>
            {Lsectitle("Análisis CT = CI + CJ")}
            {Lformula(`CJ = I² × R × L × F   I_diseño=${fmtN(Itotal,2)}A   L=${long}km\nF = (T×P+D)×Q/(1+i) = ${fmtN(F_e,4)}   CT = CI + CJ`)}
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                <thead>
                  <tr>{["Calibre","R (Ω/km)","CI ($)","CJ VPN ($)","CT ($)","Selección"].map(h=>(
                    <th key={h} style={{background:"#0A2463",color:"#fff",padding:"7px 8px",textAlign:"left",fontWeight:600}}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {ecoRows.map((row,i)=>(
                    <tr key={i} style={{background:i===optIdx?"#fff7ed":i%2===0?"#fafafa":"#fff",fontWeight:i===optIdx?700:400}}>
                      <td style={{padding:"6px 8px",fontFamily:"'Courier New',monospace",fontWeight:600}}>{row.c.name}</td>
                      <td style={{padding:"6px 8px"}}>{row.c.r}</td>
                      <td style={{padding:"6px 8px"}}>{fmtCOP(row.CI)}</td>
                      <td style={{padding:"6px 8px"}}>{fmtCOP(row.CJ)}</td>
                      <td style={{padding:"6px 8px",fontWeight:700}}>{fmtCOP(row.CT)}</td>
                      <td style={{padding:"6px 8px"}}>{i===optIdx?<LBadge ok>✓ Óptimo</LBadge>:<span style={{color:"#ccc"}}>—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {optIdx!==null&&(
              <div style={{marginTop:10,padding:"10px 14px",background:"#fff7ed",borderRadius:7,border:"1.5px solid #fed7aa",fontSize:12}}>
                <strong style={{color:"#c2410c"}}>Conductor óptimo: </strong>
                <span style={{fontFamily:"'Courier New',monospace",fontWeight:700}}>{ecoRows[optIdx]?.c.name}</span>
                {" · CT = "}{fmtCOP(ecoRows[optIdx]?.CT)}
                <span style={{fontSize:10,color:"#9a3412",marginLeft:8}}>Verificar mínimo técnico en pestaña 2</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── TAB TRAMOS ─── */}
      {tab==="tramos" && (
        <div>
          <div style={{display:"flex",gap:8,marginBottom:14,alignItems:"center"}}>
            <button onClick={()=>setTramos(t=>[...t,{id:Date.now(),desc:`Tramo ${t.length+1}`,s:10,long:0.05,calibre:"#12 AWG",ciKm:1500000}])}
              style={{...S.btnPrimary,fontSize:12}}>+ Agregar tramo</button>
            <button onClick={()=>setTab("resumen")} style={{...S.btnOutline,fontSize:12}}>Ver resumen →</button>
            <span style={{fontSize:11,color:"#888"}}>{tramos.length} tramo(s)</span>
          </div>
          {tramos.length===0&&(
            <div style={{...Lcard,textAlign:"center",padding:"40px 20px",color:"#9ca3af"}}>
              <div style={{fontSize:28,marginBottom:8}}>📐</div>
              <div style={{fontWeight:600,fontSize:13}}>Sin tramos definidos</div>
              <div style={{fontSize:12,marginTop:4}}>Haga clic en "Agregar tramo" para comenzar</div>
            </div>
          )}
          {tramos.map((t,idx)=>(
            <div key={t.id} style={{...Lcard,marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <span style={{background:"#dbeafe",color:"#1e40af",fontSize:10,fontWeight:700,padding:"2px 9px",borderRadius:5}}>T-{idx+1}</span>
                <button onClick={()=>setTramos(v=>v.filter(x=>x.id!==t.id))}
                  style={{...S.btnOutline,fontSize:10,padding:"4px 10px",color:"#dc2626",borderColor:"#fecaca"}}>Eliminar</button>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 2fr 1fr",gap:8}}>
                {Lfield("Descripción", <input type="text" value={t.desc} onChange={e=>setTramos(v=>v.map(x=>x.id===t.id?{...x,desc:e.target.value}:x))}
                  style={{width:"100%",height:32,padding:"0 8px",border:"1.5px solid #e5e7eb",borderRadius:6,background:"#fff",color:"#111",fontSize:12}}/>)}
                {Lfield("S (kVA)", <input type="number" value={t.s} step={0.5} onChange={e=>setTramos(v=>v.map(x=>x.id===t.id?{...x,s:+e.target.value}:x))}
                  style={{width:"100%",height:32,padding:"0 8px",border:"1.5px solid #e5e7eb",borderRadius:6,background:"#fff",color:"#111",fontSize:12,fontFamily:"'Courier New',monospace"}}/>)}
                {Lfield("Long. (km)", <input type="number" value={t.long} step={0.001} onChange={e=>setTramos(v=>v.map(x=>x.id===t.id?{...x,long:+e.target.value}:x))}
                  style={{width:"100%",height:32,padding:"0 8px",border:"1.5px solid #e5e7eb",borderRadius:6,background:"#fff",color:"#111",fontSize:12,fontFamily:"'Courier New',monospace"}}/>)}
                {Lfield("Calibre", <select value={t.calibre} onChange={e=>setTramos(v=>v.map(x=>x.id===t.id?{...x,calibre:e.target.value}:x))}
                  style={{width:"100%",height:32,padding:"0 8px",border:"1.5px solid #e5e7eb",borderRadius:6,background:"#fff",color:"#111",fontSize:12}}>
                  {CALIBRES_CALC.map(c=><option key={c.awg}>{c.name}</option>)}
                </select>)}
                {Lfield("CI ($/km)", <input type="number" value={t.ciKm} step={50000} onChange={e=>setTramos(v=>v.map(x=>x.id===t.id?{...x,ciKm:+e.target.value}:x))}
                  style={{width:"100%",height:32,padding:"0 8px",border:"1.5px solid #e5e7eb",borderRadius:6,background:"#fff",color:"#111",fontSize:12,fontFamily:"'Courier New',monospace"}}/>)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ─── TAB RESUMEN ─── */}
      {tab==="resumen" && (
        <div>
          {tramos.length===0?(
            <div style={{...Lcard,textAlign:"center",padding:"40px",color:"#9ca3af"}}>
              Define tramos en la pestaña 4 para ver el resumen del proyecto.
            </div>
          ):(
            <>
              {/* Métricas totales */}
              <div style={Lgrid3}>
                {Lmetric("Total CI proyecto", fmtCOP(totCI))}
                {Lmetric("Total CJ (valor presente)", fmtCOP(totCJ))}
                {Lmetric("Costo Total CT", fmtCOP(totCT))}
              </div>

              {/* Botones de exportación */}
              <div style={{...Lcard,marginTop:12,background:"#eff6ff",border:"1.5px solid #bfdbfe"}}>
                <div style={{fontSize:11,fontWeight:700,color:"#0A2463",marginBottom:10,letterSpacing:".05em",textTransform:"uppercase"}}>
                  📋 Exportar resultados a la plantilla Word
                </div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                  <button
                    onClick={()=>exportarWordCalc({sys,eco,tramo,tramosCalc,ecoRows,tecRows,optIdx,Fp,r_f,Q_f,T_f,Inom,Idis,Itotal,Sicc,XL,k_mat,margen,totCI,totCJ,totCT,fmtN,fmtCOP})}
                    style={{...S.btnPrimary,fontSize:12,background:"#0A2463",display:"flex",alignItems:"center",gap:6}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
                    Exportar Word (.docx) — Plantilla completa
                  </button>
                  <button
                    onClick={()=>copiarTablaCalc({sys,eco,tramo,tramosCalc,ecoRows,tecRows,optIdx,Fp,r_f,Q_f,T_f,Inom,Idis,Itotal,Sicc,k_mat,margen,totCI,totCJ,totCT,fmtN,fmtCOP})}
                    style={{...S.btnOutline,fontSize:12,display:"flex",alignItems:"center",gap:6}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                    Copiar tabla al portapapeles
                  </button>
                </div>
                <div style={{fontSize:10,color:"#3b82f6",marginTop:8}}>
                  Word: genera el .docx con todas las secciones rellenas — ábrelo en Word y pégalo en tu memoria de cálculo.
                  Copiar: copia la tabla en formato texto para pegar directamente en el Word existente.
                </div>
              </div>

              {/* Tabla resumen tramos */}
              <div style={{...Lcard,marginTop:12}}>
                {Lsectitle("Tabla resumen por tramo")}
                <div style={{overflowX:"auto"}} id="tabla-resumen-tramos">
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                    <thead><tr>{["T","Descripción","S (kVA)","L (km)","Calibre adoptado","I diseño (A)","ΔV (%)","Estado técnico","CI ($)","CJ ($)","CT ($)"].map(h=>(
                      <th key={h} style={{background:"#0A2463",color:"#fff",padding:"7px 8px",textAlign:"left",fontWeight:600,whiteSpace:"nowrap"}}>{h}</th>
                    ))}</tr></thead>
                    <tbody>
                      {tramosCalc.map((r,i)=>(
                        <tr key={i} style={{background:i%2===0?"#fafafa":"#fff"}}>
                          <td style={{padding:"6px 8px",fontWeight:700,color:"#0A2463"}}>T-{i+1}</td>
                          <td style={{padding:"6px 8px"}}>{r.desc}</td>
                          <td style={{padding:"6px 8px"}}>{r.s}</td>
                          <td style={{padding:"6px 8px"}}>{r.long}</td>
                          <td style={{padding:"6px 8px",fontFamily:"'Courier New',monospace",fontWeight:600}}>{r.calibre}</td>
                          <td style={{padding:"6px 8px"}}>{fmtN(r.it2,1)}</td>
                          <td style={{padding:"6px 8px",color:r.dv2>5?"#dc2626":"#15803d"}}>{fmtN(r.dv2,2)}</td>
                          <td style={{padding:"6px 8px"}}><LBadge ok={r.okAmp&&r.okDv}>{r.okAmp&&r.okDv?"✓ Cumple":"✗ Revisar"}</LBadge></td>
                          <td style={{padding:"6px 8px"}}>{fmtCOP(r.CI)}</td>
                          <td style={{padding:"6px 8px"}}>{fmtCOP(r.CJ)}</td>
                          <td style={{padding:"6px 8px",fontWeight:700}}>{fmtCOP(r.CT)}</td>
                        </tr>
                      ))}
                      <tr style={{background:"#eff6ff",fontWeight:700}}>
                        <td colSpan={8} style={{padding:"7px 8px",color:"#0A2463"}}>TOTAL PROYECTO</td>
                        <td style={{padding:"7px 8px"}}>{fmtCOP(totCI)}</td>
                        <td style={{padding:"7px 8px"}}>{fmtCOP(totCJ)}</td>
                        <td style={{padding:"7px 8px",color:"#0A2463"}}>{fmtCOP(totCT)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Conductor óptimo */}
              {optIdx!==null&&(
                <div style={{...Lcard,marginTop:12,background:"#fff7ed",border:"1.5px solid #fed7aa"}}>
                  {Lsectitle("Conductor óptimo económico — justificación")}
                  <div style={{fontSize:12,lineHeight:1.8}}>
                    <div><strong>Calibre óptimo:</strong> <span style={{fontFamily:"'Courier New',monospace",fontWeight:700,fontSize:13}}>{ecoRows[optIdx]?.c.name}</span></div>
                    <div><strong>Costo instalación CI:</strong> {fmtCOP(ecoRows[optIdx]?.CI)}</div>
                    <div><strong>Costo operativo CJ (VPN {eco.vidaUtil} años):</strong> {fmtCOP(ecoRows[optIdx]?.CJ)}</div>
                    <div><strong>Costo total CT = CI + CJ:</strong> {fmtCOP(ecoRows[optIdx]?.CT)}</div>
                    <div style={{marginTop:8,fontSize:11,color:"#9a3412",padding:"8px 10px",background:"#fff",borderRadius:6,border:"1px solid #fed7aa"}}>
                      El conductor {ecoRows[optIdx]?.c.name} fue seleccionado porque minimiza el costo total (CI + CJ)
                      durante la vida útil de {eco.vidaUtil} años, con tasa de descuento i={eco.tasa}%,
                      aumento anual de carga a={eco.aumCarga}% y costo de energía P=${eco.costoP}/kWh.
                      {tecRows[optIdx]?.okAmp && tecRows[optIdx]?.okIcc
                        ? " Cumple todos los requisitos técnicos: ampacidad, regulación y corriente de cortocircuito."
                        : " ⚠ Verificar que cumple mínimo técnico — puede requerir calibre superior."}
                    </div>
                  </div>
                </div>
              )}

              {/* Parámetros de cálculo */}
              <div style={{...Lcard,marginTop:12}}>
                {Lsectitle("Parámetros del sistema y económicos")}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,fontSize:11}}>
                  <div>
                    <div style={{fontWeight:700,color:"#0A2463",marginBottom:6}}>Sistema eléctrico</div>
                    {[["Tipo de sistema",sys.tipo],["Nivel de tensión",sys.nivel],["Tensión de línea VL",sys.vl+" V"],
                      ["Factor de potencia",sys.fp],["Factor de carga FC",sys.fc],["THDi",sys.thdi+"%"],
                      ["Temperatura ambiente",sys.tempAmb+"°C"],["Margen de seguridad",`×${margen} (${sys.nivel})`]
                    ].map(([l,v])=>(
                      <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"3px 0",borderBottom:"1px solid #f3f4f6"}}>
                        <span style={{color:"#6b7280"}}>{l}</span>
                        <span style={{fontWeight:600,fontFamily:"'Courier New',monospace"}}>{v}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div style={{fontWeight:700,color:"#0A2463",marginBottom:6}}>Parámetros económicos (Ec. 25-27)</div>
                    {[["Tasa de descuento i",eco.tasa+"%"],["Aumento anual carga a",eco.aumCarga+"%"],
                      ["Aumento costo energía b",eco.aumEnergia+"%"],["Costo energía P",`$${eco.costoP}/kWh`],
                      ["Costo adicional D",`$${eco.costoD}/kWh`],["Horas/día",eco.horasDia+" h"],
                      ["Vida útil N",eco.vidaUtil+" años"],["Factor pérdidas Fp",fmtN(Fp,4)],
                      ["Factor r (Ec.27)",fmtN(r_f,4)],["Factor Q (Ec.26)",fmtN(Q_f,2)],
                      ["Horas anuales T",Math.round(T_f)+" h/año"]
                    ].map(([l,v])=>(
                      <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"3px 0",borderBottom:"1px solid #f3f4f6"}}>
                        <span style={{color:"#6b7280"}}>{l}</span>
                        <span style={{fontWeight:600,fontFamily:"'Courier New',monospace"}}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Resultados técnicos */}
              <div style={{...Lcard,marginTop:12}}>
                {Lsectitle("Resultados técnicos del tramo analizado")}
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,fontSize:11}}>
                  {[["I nominal (Ec.1-3)",fmtN(Inom,2),"A"],["I diseño ×"+margen,fmtN(Idis,2),"A"],
                    ["I total con THD (Ec.6)",fmtN(Itotal,2),"A"],["Sección mín. Icc (Ec.21)",fmtN(Sicc,2),"mm²"],
                    ["Reactancia X (Ec.33)",fmtN(XL,4),"Ω/km"],["k conductor ("+tramo.material+")",k_mat,"—"]
                  ].map(([l,v,u])=>(
                    <div key={l} style={{background:"#f8fafc",borderRadius:7,padding:"10px 12px",border:"1px solid #f1f5f9"}}>
                      <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:".05em",color:"#6b7280",marginBottom:3}}>{l}</div>
                      <div style={{fontSize:18,fontWeight:700,color:"#0A2463",fontFamily:"'Courier New',monospace"}}>{v}</div>
                      <div style={{fontSize:10,color:"#9ca3af"}}>{u}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Verificación por calibre */}
              <div style={{...Lcard,marginTop:12}}>
                {Lsectitle("Verificación técnica por calibre — corrección temperatura")}
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:10}}>
                    <thead><tr>{["Calibre","Área mm²","Amp.75°C","Amp.corr.","R Ω/km","ΔV%","Amp≥Itot","Área≥Icc","Estado"].map(h=>(
                      <th key={h} style={{background:"#0A2463",color:"#fff",padding:"6px 7px",textAlign:"left",fontWeight:600,whiteSpace:"nowrap"}}>{h}</th>
                    ))}</tr></thead>
                    <tbody>
                      {tecRows.map((row,i)=>(
                        <tr key={i} style={{background:row.okAmp&&row.okIcc?"#f0fdf4":i%2===0?"#fafafa":"#fff"}}>
                          <td style={{padding:"5px 7px",fontWeight:700,fontFamily:"'Courier New',monospace"}}>{row.c.name}</td>
                          <td style={{padding:"5px 7px"}}>{row.c.area}</td>
                          <td style={{padding:"5px 7px"}}>{row.c.amp75}A</td>
                          <td style={{padding:"5px 7px"}}>{fmtN(row.ampC,0)}A</td>
                          <td style={{padding:"5px 7px"}}>{row.c.r}</td>
                          <td style={{padding:"5px 7px",color:row.dv>5?"#dc2626":"#15803d"}}>{fmtN(row.dv,2)}%</td>
                          <td style={{padding:"5px 7px"}}><LBadge ok={row.okAmp}>{row.okAmp?"✓":"✗"}</LBadge></td>
                          <td style={{padding:"5px 7px"}}><LBadge ok={row.okIcc}>{row.okIcc?"✓":"✗"}</LBadge></td>
                          <td style={{padding:"5px 7px"}}><LBadge ok={row.okAmp&&row.okIcc} warn={row.okAmp&&!row.okIcc}>{row.okAmp&&row.okIcc?"Cumple":row.okAmp?"Ins.Icc":"Ins.amp."}</LBadge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{marginTop:12,display:"flex",gap:8}}>
                <button
                  onClick={()=>exportarWordCalc({sys,eco,tramo,tramosCalc,ecoRows,tecRows,optIdx,Fp,r_f,Q_f,T_f,Inom,Idis,Itotal,Sicc,XL,k_mat,margen,totCI,totCJ,totCT,fmtN,fmtCOP})}
                  style={{...S.btnPrimary,fontSize:12,display:"flex",alignItems:"center",gap:6}}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
                  Exportar Word (.docx)
                </button>
                <button
                  onClick={()=>copiarTablaCalc({sys,eco,tramo,tramosCalc,ecoRows,tecRows,optIdx,Fp,r_f,Q_f,T_f,Inom,Idis,Itotal,Sicc,k_mat,margen,totCI,totCJ,totCT,fmtN,fmtCOP})}
                  style={{...S.btnOutline,fontSize:12,display:"flex",alignItems:"center",gap:6}}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                  Copiar tabla (pegar en Word)
                </button>
                <button onClick={()=>setTab("tramos")} style={{...S.btnOutline,fontSize:12}}>← Editar tramos</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// EXPORTAR WORD — genera .docx sin dependencias externas
// Usa Open XML (OOXML) directamente con Blob + base64
// ══════════════════════════════════════════════════════════════
function exportarWordCalc(p) {
  const {sys,eco,tramo,tramosCalc,ecoRows,tecRows,optIdx,Fp,r_f,Q_f,T_f,
         Inom,Idis,Itotal,Sicc,XL,k_mat,margen,totCI,totCJ,totCT,fmtN,fmtCOP} = p;

  const fecha = new Date().toLocaleDateString("es-CO");

  // ── helpers XML ──────────────────────────────────────────────
  const esc = s => String(s)
    .replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")
    .replace(/"/g,"&quot;");

  const azul = "0A2463", naranja = "FF6B00", azulC = "E8F1FD", gris = "F5F5F5";

  const run = (text, opts={}) => {
    const b   = opts.bold   ? "<w:b/><w:bCs/>"  : "";
    const sz  = opts.size   ? `<w:sz w:val="${opts.size*2}"/><w:szCs w:val="${opts.size*2}"/>` : "";
    const col = opts.color  ? `<w:color w:val="${opts.color}"/>` : "";
    const font= `<w:rFonts w:ascii="Arial" w:hAnsi="Arial" w:cs="Arial"/>`;
    const mono= opts.mono   ? `<w:rFonts w:ascii="Courier New" w:hAnsi="Courier New" w:cs="Courier New"/>` : font;
    return `<w:r><w:rPr>${b}${sz}${col}${opts.mono?mono:font}</w:rPr><w:t xml:space="preserve">${esc(text)}</w:t></w:r>`;
  };

  const para = (runs, opts={}) => {
    const align = opts.center ? `<w:jc w:val="center"/>` : "";
    const shade = opts.shade  ? `<w:shd w:val="clear" w:color="auto" w:fill="${opts.shade}"/>` : "";
    const bef   = opts.before ? `w:before="${opts.before}"` : "";
    const aft   = opts.after  ? `w:after="${opts.after}"`   : "";
    const bdr   = opts.bdrLeft? `<w:pBdr><w:left w:val="single" w:sz="24" w:space="4" w:color="${naranja}"/></w:pBdr>` : "";
    const spacing = (bef||aft) ? `<w:spacing ${bef} ${aft}/>` : "";
    return `<w:p><w:pPr>${align}${shade}${bdr}${spacing}</w:pPr>${runs}</w:p>`;
  };

  const secTitle = (num, title) => para(
    run(`${num}  ${title.toUpperCase()}`, {bold:true, size:11, color:"FFFFFF"}),
    {shade:azul, before:"200", after:"100", bdrLeft:true}
  );

  const subTitle = (t) => para(
    run(t, {bold:true, size:11, color:azul}),
    {before:"120", after:"60"}
  );

  // Celda de tabla
  const tc = (text, opts={}) => {
    const fill = opts.fill ? `<w:shd w:val="clear" w:color="auto" w:fill="${opts.fill}"/>` : "";
    const span = opts.span ? `<w:gridSpan w:val="${opts.span}"/>` : "";
    const vert = opts.vert ? `<w:vAlign w:val="center"/>` : "";
    const w    = opts.w    ? `<w:tcW w:w="${opts.w}" w:type="dxa"/>` : `<w:tcW w:w="1404" w:type="dxa"/>`;
    const bld  = opts.bold || false;
    const col  = opts.color|| (opts.fill===azul?"FFFFFF":undefined);
    const mono = opts.mono || false;
    const sz   = opts.size || 9;
    return `<w:tc><w:tcPr>${w}${fill}${span}${vert}</w:tcPr>${para(run(esc(text),{bold:bld,size:sz,color:col,mono}),{after:"60"})}</w:tc>`;
  };

  const tr = (cells) => `<w:tr>${cells}</w:tr>`;

  const tableStart = (cols) => {
    const grid = cols.map(w=>`<w:gridCol w:w="${w}"/>`).join("");
    return `<w:tbl><w:tblPr><w:tblW w:w="9360" w:type="dxa"/><w:tblBorders>
      <w:top w:val="single" w:sz="4" w:color="CCCCCC"/>
      <w:left w:val="single" w:sz="4" w:color="CCCCCC"/>
      <w:bottom w:val="single" w:sz="4" w:color="CCCCCC"/>
      <w:right w:val="single" w:sz="4" w:color="CCCCCC"/>
      <w:insideH w:val="single" w:sz="4" w:color="CCCCCC"/>
      <w:insideV w:val="single" w:sz="4" w:color="CCCCCC"/>
    </w:tblBorders></w:tblPr><w:tblGrid>${grid}</w:tblGrid>`;
  };
  const tableEnd = () => `</w:tbl>`;

  const pb = () => `<w:p><w:r><w:br w:type="page"/></w:r></w:p>`;

  // ── Construir el documento ──────────────────────────────────
  let body = "";

  // Portada
  body += para(run("ÍTEMS (j) Y (k) — CÁLCULO ECONÓMICO DE CONDUCTORES",{bold:true,size:20,color:azul}),{center:true,before:"400",after:"80"});
  body += para(run("Metodología EPM GM-01 · GTC 221 · RETIE 2024",{size:11,color:"555555"}),{center:true,after:"60"});
  body += para(run(`Fecha: ${fecha}`,{size:10,color:"888888"}),{center:true,after:"400"});

  // Tabla datos generales
  body += tableStart([2000,3680,2000,1680]);
  body += tr([tc("DATOS DEL PROYECTO",{fill:azul,span:4,bold:true,color:"FFFFFF",size:10,w:9360})]);
  [
    ["Tipo de sistema",sys.tipo,"Nivel de tensión",sys.nivel],
    ["Tensión de línea VL",sys.vl+" V","Factor potencia cosφ",sys.fp],
    ["Factor de carga FC",sys.fc,"THDi",sys.thdi+"%"],
    ["Temperatura ambiente",sys.tempAmb+"°C","Margen seguridad","×"+margen+" ("+sys.nivel+")"],
  ].forEach(row=>{
    body += tr([
      tc(row[0],{fill:azulC,bold:true,size:9,w:2000}),tc(row[1],{size:9,w:3680,mono:true}),
      tc(row[2],{fill:azulC,bold:true,size:9,w:2000}),tc(row[3],{size:9,w:1680,mono:true})
    ]);
  });
  body += tableEnd();
  body += pb();

  // 1. Parámetros económicos
  body += secTitle("1.","Parámetros económicos (EPM GM-01 Ec. 25-27)");
  body += tableStart([3200,2080,3200,880]);
  body += tr([tc("Parámetro",{fill:azul,bold:true,color:"FFFFFF",size:9,w:3200}),tc("Valor",{fill:azul,bold:true,color:"FFFFFF",size:9,w:2080}),tc("Parámetro",{fill:azul,bold:true,color:"FFFFFF",size:9,w:3200}),tc("Valor",{fill:azul,bold:true,color:"FFFFFF",size:9,w:880})]);
  const ecoParams = [
    ["Tasa de descuento i",eco.tasa+"%","Factor pérdidas Fp = 0,7·FC²+0,3·FC",fmtN(Fp,4)],
    ["Aumento anual carga a",eco.aumCarga+"%","Factor r = (1+a)²·(1+b)/(1+i)",fmtN(r_f,4)],
    ["Aumento costo energía b",eco.aumEnergia+"%","Factor Q = Σ r^(n-1)",fmtN(Q_f,2)],
    ["Costo energía P","$"+eco.costoP+"/kWh","Horas anuales T",Math.round(T_f)+" h/año"],
    ["Costo adicional D","$"+eco.costoD+"/kWh","",""],
    ["Vida útil N",eco.vidaUtil+" años","",""],
  ];
  ecoParams.forEach((row,i)=>{
    const fill = i%2===0?azulC:undefined;
    body += tr([tc(row[0],{fill,size:9,w:3200}),tc(row[1],{fill,size:9,w:2080,mono:true}),tc(row[2],{fill,size:9,w:3200}),tc(row[3],{fill,size:9,w:880,mono:true})]);
  });
  body += tableEnd();

  // 2. Resultados técnicos
  body += para("",{after:"120"});
  body += secTitle("2.","Resultados técnicos del tramo analizado");
  body += tableStart([3120,1560,2340,2340]);
  body += tr([tc("Parámetro",{fill:azul,bold:true,color:"FFFFFF",size:9,w:3120}),tc("Símbolo",{fill:azul,bold:true,color:"FFFFFF",size:9,w:1560}),tc("Valor",{fill:azul,bold:true,color:"FFFFFF",size:9,w:2340}),tc("Ecuación",{fill:azul,bold:true,color:"FFFFFF",size:9,w:2340})]);
  const tecParams = [
    ["Corriente nominal","I_nom",fmtN(Inom,2)+" A","Ec. 1-3"],
    ["Corriente de diseño (×"+margen+")","I_dis",fmtN(Idis,2)+" A","Ec. 4/5"],
    ["Corriente total con THD","I_tot",fmtN(Itotal,2)+" A","Ec. 6"],
    ["Sección mínima por Icc","A_min",fmtN(Sicc,2)+" mm²","Ec. 21"],
    ["Reactancia inductiva","X_L",fmtN(XL,4)+" Ω/km","Ec. 33"],
    ["Coeficiente conductor","k",String(k_mat),"Tabla GM-01"],
  ];
  tecParams.forEach((row,i)=>{
    const fill = i%2===0?azulC:undefined;
    body += tr([tc(row[0],{fill,size:9,w:3120}),tc(row[1],{fill,size:9,w:1560,mono:true}),tc(row[2],{fill,size:9,w:2340,bold:true,mono:true}),tc(row[3],{fill,size:9,w:2340})]);
  });
  body += tableEnd();
  body += pb();

  // 3. Verificación por calibre
  body += secTitle("3.","Verificación técnica por calibre — NTC 2050 T.310-16");
  const vCols = [1300,800,900,900,800,700,900,900,1160];
  body += tableStart(vCols);
  body += tr(["Calibre","Área mm²","Amp.75°C","Amp.corr.","R Ω/km","ΔV%","Amp≥Itot","Área≥Icc","Estado"].map((h,i)=>tc(h,{fill:azul,bold:true,color:"FFFFFF",size:8,w:vCols[i]})));
  tecRows.forEach((row,i)=>{
    const fill = (row.okAmp&&row.okIcc)?"E8F5E9":i%2===0?azulC:undefined;
    const estado = row.okAmp&&row.okIcc?"Cumple":row.okAmp?"Ins. Icc":"Ins. amp.";
    body += tr([
      tc(row.c.name,{fill,size:8,bold:true,mono:true,w:vCols[0]}),
      tc(String(row.c.area),{fill,size:8,w:vCols[1]}),
      tc(row.c.amp75+"A",{fill,size:8,w:vCols[2]}),
      tc(fmtN(row.ampC,0)+"A",{fill,size:8,w:vCols[3]}),
      tc(String(row.c.r),{fill,size:8,w:vCols[4]}),
      tc(fmtN(row.dv,2)+"%",{fill,size:8,w:vCols[5]}),
      tc(row.okAmp?"✓":"✗",{fill,size:8,w:vCols[6]}),
      tc(row.okIcc?"✓":"✗",{fill,size:8,w:vCols[7]}),
      tc(estado,{fill:row.okAmp&&row.okIcc?"B7E1CD":row.okAmp?"FFF3CD":"FFCDD2",size:8,bold:true,w:vCols[8]}),
    ]);
  });
  body += tableEnd();
  body += pb();

  // 4. Análisis económico CT = CI + CJ
  body += secTitle("4.","Análisis económico CT = CI + CJ — valor presente");
  body += para(run(`CJ = I² × R × L × F   ·   F = Np×Nc×(T×P+D)×Q/(1+i)   ·   I_diseño = ${fmtN(Itotal,2)} A`,{size:9,color:"1e3a8a",mono:true}),{shade:"EFF6FF",before:"60",after:"80",bdrLeft:true});
  const eCols = [1400,900,1800,1900,1960,1400];
  body += tableStart(eCols);
  body += tr(["Calibre","R (Ω/km)","CI ($)","CJ VPN ($)","CT = CI+CJ ($)","Selección"].map((h,i)=>tc(h,{fill:azul,bold:true,color:"FFFFFF",size:9,w:eCols[i]})));
  ecoRows.forEach((row,i)=>{
    const isOpt = i===optIdx;
    const fill = isOpt?"FFF7ED":i%2===0?azulC:undefined;
    body += tr([
      tc(row.c.name,{fill,size:9,bold:true,mono:true,w:eCols[0]}),
      tc(String(row.c.r),{fill,size:9,w:eCols[1]}),
      tc(fmtCOP(row.CI),{fill,size:9,w:eCols[2]}),
      tc(fmtCOP(row.CJ),{fill,size:9,w:eCols[3]}),
      tc(fmtCOP(row.CT),{fill,size:9,bold:true,w:eCols[4]}),
      tc(isOpt?"✓ ÓPTIMO":"—",{fill:isOpt?"FED7AA":fill,size:9,bold:isOpt,w:eCols[5]}),
    ]);
  });
  body += tableEnd();

  // Justificación conductor óptimo
  if(optIdx!==null){
    const opt = ecoRows[optIdx];
    body += para("",{after:"80"});
    body += subTitle("Conductor óptimo — justificación");
    body += para(
      run(`Calibre seleccionado: `,{bold:true,size:10})+
      run(opt.c.name,{bold:true,size:12,color:naranja,mono:true})+
      run(`  ·  CT = ${fmtCOP(opt.CT)}`,{size:10}),
      {shade:"FFF7ED",before:"60",after:"60",bdrLeft:true}
    );
    body += para(
      run(`El conductor ${opt.c.name} fue seleccionado porque minimiza el costo total (CI + CJ) durante la vida útil de ${eco.vidaUtil} años, con tasa de descuento i=${eco.tasa}%, aumento anual de carga a=${eco.aumCarga}% y costo de energía P=$${eco.costoP}/kWh. `,{size:10})+
      run(tecRows[optIdx]?.okAmp&&tecRows[optIdx]?.okIcc
        ?"Cumple todos los requisitos técnicos: ampacidad, regulación y corriente de cortocircuito (RETIE 2024 Ítems j y k)."
        :"VERIFICAR: el óptimo económico puede no cumplir el mínimo técnico — evaluar calibre superior.",
        {size:10,bold:!tecRows[optIdx]?.okAmp||!tecRows[optIdx]?.okIcc}),
      {after:"80"}
    );
  }
  body += pb();

  // 5. Resumen por tramo
  body += secTitle("5.","Resumen de resultados por tramo");
  const rCols=[500,1500,700,700,1200,800,700,700,900,900,960];
  body += tableStart(rCols);
  body += tr(["T","Descripción","S kVA","L km","Calibre","I dis. A","ΔV%","Estado","CI $","CJ $","CT $"].map((h,i)=>tc(h,{fill:azul,bold:true,color:"FFFFFF",size:8,w:rCols[i]})));
  tramosCalc.forEach((r,i)=>{
    const fill = i%2===0?azulC:undefined;
    const ok = r.okAmp&&r.okDv;
    body += tr([
      tc("T-"+(i+1),{fill,size:8,bold:true,w:rCols[0]}),
      tc(r.desc,{fill,size:8,w:rCols[1]}),
      tc(String(r.s),{fill,size:8,w:rCols[2]}),
      tc(String(r.long),{fill,size:8,w:rCols[3]}),
      tc(r.calibre,{fill,size:8,bold:true,mono:true,w:rCols[4]}),
      tc(fmtN(r.it2,1),{fill,size:8,w:rCols[5]}),
      tc(fmtN(r.dv2,2)+"%",{fill:r.dv2>5?"FFCDD2":fill,size:8,w:rCols[6]}),
      tc(ok?"✓ OK":"✗ Rev.",{fill:ok?"B7E1CD":"FFCDD2",size:8,bold:true,w:rCols[7]}),
      tc(fmtCOP(r.CI),{fill,size:8,w:rCols[8]}),
      tc(fmtCOP(r.CJ),{fill,size:8,w:rCols[9]}),
      tc(fmtCOP(r.CT),{fill,size:8,bold:true,w:rCols[10]}),
    ]);
  });
  // Total
  body += tr([
    tc("TOTAL",{fill:azulC,bold:true,size:9,w:rCols[0]}),
    tc("PROYECTO",{fill:azulC,bold:true,size:9,span:7,w:5800}),
    tc(fmtCOP(totCI),{fill:azulC,bold:true,size:9,w:rCols[8]}),
    tc(fmtCOP(totCJ),{fill:azulC,bold:true,size:9,w:rCols[9]}),
    tc(fmtCOP(totCT),{fill:azulC,bold:true,size:9,color:azul,w:rCols[10]}),
  ]);
  body += tableEnd();

  // Nota pie
  body += para("",{after:"80"});
  body += para(
    run(`Parámetros: N=${eco.vidaUtil}a · i=${eco.tasa}% · a=${eco.aumCarga}% · b=${eco.aumEnergia}% · FC=${sys.fc} · THDi=${sys.thdi}% · ${eco.horasDia}h/día · VL=${sys.vl}V · Fp=${fmtN(Fp,4)} · Q=${fmtN(Q_f,2)} · Generado: ${fecha}`,
      {size:8,color:"888888",mono:true}),
    {shade:gris,after:"60"}
  );

  // ── Ensamblar .docx (OOXML mínimo válido) ──────────────────
  const docXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:wpc="http://schemas.microsoft.com/office/word/2010/wordprocessingCanvas"
  xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"
  xmlns:w14="http://schemas.microsoft.com/office/word/2010/wordml">
<w:body>
${body}
<w:sectPr>
  <w:pgSz w:w="12240" w:h="15840"/>
  <w:pgMar w:top="1200" w:right="1200" w:bottom="1200" w:left="1200" w:header="720" w:footer="720"/>
</w:sectPr>
</w:body></w:document>`;

  const relsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

  const stylesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:docDefaults><w:rPrDefault><w:rPr>
    <w:rFonts w:ascii="Arial" w:hAnsi="Arial"/>
    <w:sz w:val="20"/><w:szCs w:val="20"/>
  </w:rPr></w:rPrDefault></w:docDefaults>
</w:styles>`;

  const contentTypesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
  <Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>
</Types>`;

  const rootRelsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`;

  // Empaquetar ZIP manualmente (OOXML = ZIP)
  const files = {
    "[Content_Types].xml": contentTypesXml,
    "_rels/.rels": rootRelsXml,
    "word/document.xml": docXml,
    "word/styles.xml": stylesXml,
    "word/_rels/document.xml.rels": relsXml,
  };

  // Crear ZIP usando JSZip-like encoding nativo
  const zipBlob = crearZip(files);
  const url = URL.createObjectURL(zipBlob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `EH_Conductor-Economico_GM01_${fecha.replace(/\//g,"-")}.docx`;
  a.click();
  setTimeout(()=>URL.revokeObjectURL(url), 3000);
}

// ── Mini ZIP encoder (formato ZIP sin compresión — suficiente para .docx) ──
function crearZip(files) {
  const enc = new TextEncoder();
  const parts = [];
  const centralDir = [];
  let offset = 0;

  for (const [name, content] of Object.entries(files)) {
    const nameBytes  = enc.encode(name);
    const dataBytes  = enc.encode(content);
    const crc        = crc32(dataBytes);
    const localHeader = buildLocalHeader(nameBytes, dataBytes, crc);
    parts.push(localHeader, dataBytes);
    centralDir.push(buildCentralHeader(nameBytes, dataBytes, crc, offset));
    offset += localHeader.byteLength + dataBytes.byteLength;
  }

  const cdBytes  = concat(centralDir);
  const eocd     = buildEOCD(Object.keys(files).length, cdBytes.byteLength, offset);
  const allParts = [...parts, cdBytes, eocd];
  return new Blob(allParts, {type:"application/vnd.openxmlformats-officedocument.wordprocessingml.document"});
}

function buildLocalHeader(name, data, crc) {
  const b = new DataView(new ArrayBuffer(30 + name.byteLength));
  b.setUint32(0,  0x04034b50, true); // signature
  b.setUint16(4,  20, true);         // version needed
  b.setUint16(6,  0,  true);         // flags
  b.setUint16(8,  0,  true);         // compression (stored)
  b.setUint16(10, 0,  true);         // mod time
  b.setUint16(12, 0,  true);         // mod date
  b.setUint32(14, crc, true);
  b.setUint32(18, data.byteLength, true);
  b.setUint32(22, data.byteLength, true);
  b.setUint16(26, name.byteLength, true);
  b.setUint16(28, 0, true);
  new Uint8Array(b.buffer).set(name, 30);
  return b.buffer;
}

function buildCentralHeader(name, data, crc, offset) {
  const b = new DataView(new ArrayBuffer(46 + name.byteLength));
  b.setUint32(0,  0x02014b50, true);
  b.setUint16(4,  20, true); b.setUint16(6, 20, true);
  b.setUint16(8,  0, true);  b.setUint16(10, 0, true);
  b.setUint16(12, 0, true);  b.setUint16(14, 0, true);
  b.setUint32(16, crc, true);
  b.setUint32(20, data.byteLength, true);
  b.setUint32(24, data.byteLength, true);
  b.setUint16(28, name.byteLength, true);
  b.setUint16(30, 0, true); b.setUint16(32, 0, true);
  b.setUint16(34, 0, true); b.setUint16(36, 0, true);
  b.setUint32(38, 0, true); b.setUint32(42, offset, true);
  new Uint8Array(b.buffer).set(name, 46);
  return b.buffer;
}

function buildEOCD(count, cdSize, cdOffset) {
  const b = new DataView(new ArrayBuffer(22));
  b.setUint32(0, 0x06054b50, true);
  b.setUint16(4, 0, true); b.setUint16(6, 0, true);
  b.setUint16(8, count, true); b.setUint16(10, count, true);
  b.setUint32(12, cdSize, true); b.setUint32(16, cdOffset, true);
  b.setUint16(20, 0, true);
  return b.buffer;
}

function concat(arrays) {
  const totalLen = arrays.reduce((s,a)=>s+(a instanceof ArrayBuffer?a.byteLength:a.byteLength),0);
  const result = new Uint8Array(totalLen);
  let pos = 0;
  for (const a of arrays) {
    const view = new Uint8Array(a instanceof ArrayBuffer?a:a);
    result.set(view, pos); pos += view.byteLength;
  }
  return result.buffer;
}

function crc32(data) {
  let crc = 0xFFFFFFFF;
  const table = crc32.table || (crc32.table = (()=>{
    const t=new Uint32Array(256);
    for(let i=0;i<256;i++){let c=i;for(let j=0;j<8;j++)c=c&1?0xEDB88320^(c>>>1):c>>>1;t[i]=c;}
    return t;
  })());
  for (let i=0; i<data.length; i++) crc = table[(crc^data[i])&0xFF]^(crc>>>8);
  return (crc^0xFFFFFFFF)>>>0;
}

// ── Copiar tabla al portapapeles ────────────────────────────────
function copiarTablaCalc(p) {
  const {sys,eco,tramo,tramosCalc,ecoRows,tecRows,optIdx,Fp,r_f,Q_f,T_f,
         Inom,Idis,Itotal,Sicc,k_mat,margen,totCI,totCJ,totCT,fmtN,fmtCOP} = p;
  const fecha = new Date().toLocaleDateString("es-CO");
  const sep = "\t";
  const nl  = "\n";
  const hr  = "─".repeat(80);

  let txt = `CÁLCULO ECONÓMICO DE CONDUCTORES — GM-01 / GTC 221 / RETIE 2024${nl}`;
  txt += `Generado: ${fecha}${nl}${hr}${nl}${nl}`;

  txt += `1. PARÁMETROS DEL SISTEMA${nl}`;
  txt += `Tipo:${sep}${sys.tipo}${sep}Nivel:${sep}${sys.nivel}${sep}VL:${sep}${sys.vl} V${nl}`;
  txt += `FC:${sep}${sys.fc}${sep}THDi:${sep}${sys.thdi}%${sep}FP:${sep}${sys.fp}${nl}`;
  txt += `i:${sep}${eco.tasa}%${sep}a:${sep}${eco.aumCarga}%${sep}b:${sep}${eco.aumEnergia}%${nl}`;
  txt += `P:${sep}$${eco.costoP}/kWh${sep}N:${sep}${eco.vidaUtil} años${sep}T:${sep}${Math.round(T_f)} h/año${nl}${nl}`;

  txt += `2. FACTORES DERIVADOS${nl}`;
  txt += `Fp=${fmtN(Fp,4)}${sep}r=${fmtN(r_f,4)}${sep}Q=${fmtN(Q_f,2)}${sep}T=${Math.round(T_f)} h/año${nl}${nl}`;

  txt += `3. RESULTADOS TÉCNICOS${nl}`;
  txt += `I_nom${sep}I_diseño (×${margen})${sep}I_tot (con THD)${sep}A_min Icc${sep}X_L${sep}k${nl}`;
  txt += `${fmtN(Inom,2)} A${sep}${fmtN(Idis,2)} A${sep}${fmtN(Itotal,2)} A${sep}${fmtN(Sicc,2)} mm²${sep}${fmtN(p.XL,4)} Ω/km${sep}${k_mat}${nl}${nl}`;

  txt += `4. VERIFICACIÓN POR CALIBRE${nl}`;
  txt += `Calibre${sep}Área mm²${sep}Amp.75°C${sep}Amp.corr.${sep}R Ω/km${sep}ΔV%${sep}Amp≥Itot${sep}Área≥Icc${sep}Estado${nl}`;
  tecRows.forEach(r=>{
    const est = r.okAmp&&r.okIcc?"Cumple":r.okAmp?"Ins. Icc":"Ins. amp.";
    txt += `${r.c.name}${sep}${r.c.area}${sep}${r.c.amp75}A${sep}${fmtN(r.ampC,0)}A${sep}${r.c.r}${sep}${fmtN(r.dv,2)}%${sep}${r.okAmp?"✓":"✗"}${sep}${r.okIcc?"✓":"✗"}${sep}${est}${nl}`;
  });
  txt += nl;

  txt += `5. ANÁLISIS ECONÓMICO CT = CI + CJ${nl}`;
  txt += `Calibre${sep}R Ω/km${sep}CI ($)${sep}CJ VPN ($)${sep}CT ($)${sep}Selección${nl}`;
  ecoRows.forEach((r,i)=>{
    txt += `${r.c.name}${sep}${r.c.r}${sep}${fmtCOP(r.CI)}${sep}${fmtCOP(r.CJ)}${sep}${fmtCOP(r.CT)}${sep}${i===optIdx?"✓ ÓPTIMO":"—"}${nl}`;
  });
  txt += nl;

  if(optIdx!==null){
    const opt = ecoRows[optIdx];
    txt += `CONDUCTOR ÓPTIMO: ${opt.c.name}  ·  CT = ${fmtCOP(opt.CT)}${nl}`;
    txt += `Justificación: minimiza CT en ${eco.vidaUtil} años con i=${eco.tasa}%, a=${eco.aumCarga}%, P=$${eco.costoP}/kWh${nl}${nl}`;
  }

  txt += `6. RESUMEN POR TRAMO${nl}`;
  txt += `T${sep}Descripción${sep}S kVA${sep}L km${sep}Calibre${sep}I diseño A${sep}ΔV%${sep}Estado${sep}CI ($)${sep}CJ ($)${sep}CT ($)${nl}`;
  tramosCalc.forEach((r,i)=>{
    txt += `T-${i+1}${sep}${r.desc}${sep}${r.s}${sep}${r.long}${sep}${r.calibre}${sep}${fmtN(r.it2,1)}${sep}${fmtN(r.dv2,2)}%${sep}${r.okAmp&&r.okDv?"✓ OK":"✗ Revisar"}${sep}${fmtCOP(r.CI)}${sep}${fmtCOP(r.CJ)}${sep}${fmtCOP(r.CT)}${nl}`;
  });
  txt += `TOTAL${sep}${sep}${sep}${sep}${sep}${sep}${sep}${sep}${fmtCOP(totCI)}${sep}${fmtCOP(totCJ)}${sep}${fmtCOP(totCT)}${nl}`;

  navigator.clipboard.writeText(txt).then(()=>{
    alert("✓ Tabla copiada al portapapeles.\n\nPega con Ctrl+V (Win) o Cmd+V (Mac) en Word.\nLas columnas separadas por tabulación se alinean automáticamente en tablas de Word.");
  }).catch(()=>{
    const el = document.createElement("textarea");
    el.value = txt;
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
    alert("✓ Tabla copiada.");
  });
}
